import { pgTable, uuid, varchar, text, boolean, timestamp, integer, decimal, json, jsonb, pgEnum, pgPolicy, pgView, date, bigint, index, uniqueIndex, check, time, unique, customType, primaryKey, vector, } from 'drizzle-orm/pg-core';
import { authenticatedRole, authUsers } from 'drizzle-orm/supabase';
import { relations, sql } from 'drizzle-orm';
import { enumToPgEnum } from '../helpers';
import { AddressSource, AvailabilityType, BillingEntityType, BusinessModels, DocumentAccessLevel, DocumentType, EntityType, EquipmentCondition, EquipmentCriticality, EquipmentOperability, EquipmentStatus, InterventionReportStatus, InterventionStatus, InterventionType, InvoiceContactType, InvoiceStatus, InvoiceType, MaintenanceType, NoteAudience, NotificationDeliveryChannel, NotificationDeliveryState, NotificationStatus, NotificationType, PriorityLevel, QuoteAutoDecisionMode, QuoteGenerationStatus, QuoteStatus, ReportPdfGenerationStatus, ReconciliationRunStatus, SiteAccessLevel, SiteContactType, SubscriptionPlan, SyncStatus, TechnicianInterventionActionType, TechnicianStatus, UserRole, UserStatus, ZoneType, } from '../enums';
import { auditTimestamps, technicianAuditTimestamps } from './base.schema';
import { INVOICE_EVENTS, } from '../events';
// Enums - Keep only core system enums
//Keep
export const userStatusEnum = pgEnum('UserStatus', enumToPgEnum(UserStatus));
//Keep
export const userRoleEnum = pgEnum('UserRole', enumToPgEnum(UserRole));
//Keep
export const legalAddressSourceEnum = pgEnum('AddressSource', enumToPgEnum(AddressSource));
//Keep
export const siteAccessLevelEnum = pgEnum('SiteAccessLevel', SiteAccessLevel);
//Keep
export const documentAccessLevelEnum = pgEnum('DocumentAccessLevel', DocumentAccessLevel);
//Keep
export const entityTypeEnum = pgEnum('EntityType', EntityType);
//Keep
export const noteAudienceEnum = pgEnum('NoteAudience', NoteAudience);
//Keep
export const interventionTypeEnum = pgEnum('InterventionType', enumToPgEnum(InterventionType));
//Keep
export const priorityLevelEnum = pgEnum('PriorityLevel', enumToPgEnum(PriorityLevel));
export const technicianStatusEnum = pgEnum('TechnicianStatus', enumToPgEnum(TechnicianStatus));
export const zoneTypeEnum = pgEnum('ZoneType', enumToPgEnum(ZoneType));
export const availabilityTypeEnum = pgEnum('AvailabilityType', enumToPgEnum(AvailabilityType));
export const interventionReportStatusEnum = pgEnum('InterventionReportStatus', enumToPgEnum(InterventionReportStatus));
// Tables
export const organizations = pgTable('organizations', {
    id: uuid('id').primaryKey().defaultRandom(),
    name: varchar('name', { length: 255 }).notNull(),
    active: boolean('active').notNull().default(true),
    // Marks THE Depanneo platform organization — the tenant every SUPER_ADMIN
    // belongs to. At most one row may carry it (`idx_organizations_platform`
    // below). It is the only machine-readable way to say "the platform org":
    // the name is not stable (« Depanneo » in prod, « Depanneo Platform » on an
    // ensure-baseline DB) and a name lookup silently picks the wrong tenant.
    // Deliberately absent from CreateOrganizationDto / UpdateOrganizationDto:
    // `organizations_update_rls` lets an ADMIN update its own org row, so an
    // exposed field would let a client ADMIN claim the marker (#1514).
    isPlatform: boolean('is_platform').notNull().default(false),
    subscriptionPlan: varchar('subscription_plan', { length: 50 })
        .$type()
        .notNull()
        .default(SubscriptionPlan.STARTER),
    // Operational address
    addressLine1: text('address_line1'),
    addressLine2: text('address_line2'),
    city: text('city'),
    postalCode: text('postal_code'),
    department: text('department'),
    region: text('region'),
    country: text('country').notNull().default('FR'),
    latitude: decimal('latitude'),
    longitude: decimal('longitude'),
    // Legal address
    legalAddressLine1: text('legal_address_line1'),
    legalAddressLine2: text('legal_address_line2'),
    legalCity: text('legal_city'),
    legalPostalCode: text('legal_postal_code'),
    legalDepartment: text('legal_department'),
    legalRegion: text('legal_region'),
    legalCountry: text('legal_country').notNull().default('FR'),
    legalSameAsOperational: boolean('legal_same_as_operational').default(true),
    gmapsPlaceId: text('gmaps_place_id'),
    // Legal information
    siren: text('siren').unique(),
    siret: text('siret').unique(),
    vatNumber: text('vat_number').unique(),
    legalName: text('legal_name'),
    companyType: varchar('company_type', { length: 50 })
        .$type()
        .notNull(),
    // Contacts
    contactEmail: text('contact_email').notNull(),
    contactPhone: text('contact_phone'),
    // Invoice
    invoiceContactType: varchar('invoice_contact_type', { length: 50 })
        .$type()
        .notNull()
        .default(InvoiceContactType.EXTERNAL),
    invoiceUserId: uuid('invoice_user_id').references(() => users.id),
    invoiceContactName: text('invoice_contact_name'),
    invoiceContactEmail: text('invoice_contact_email').notNull(),
    invoiceContactPhone: text('invoice_contact_phone'),
    billingInstructions: text('billing_instructions'),
    // billing config
    businessModels: text('business_models')
        .array()
        .notNull()
        .default([BusinessModels.B2B]),
    billingEntityTypes: text('billing_entity_types')
        .array()
        .$type()
        .notNull()
        .default([BillingEntityType.ORGANIZATION]),
    allowInterventionsWithoutSite: boolean('allow_interventions_without_site').default(false),
    responseTimeSLA: integer('response_time_sla').default(48).notNull(),
    /**
     * The org's « en dessous de ce montant » ceiling, in EUR HT.
     *
     * DECLARED TO MATCH THE DEPLOYED COLUMN — `numeric`, NULLABLE, `DEFAULT '250'`
     * since `0006_update_tables.sql` created it as `auto_trigger_threshold`
     * (`0034` only renamed it). It was `integer().default(250).notNull()` until
     * #1815.
     *
     * WHAT IT GOT WRONG — precisely, because #1586's body states it WRONGLY
     * (« the runtime value is '250' while TypeScript says number »). Not that:
     * `PgInteger.mapFromDriverValue` parseInt's the driver string, so reads did
     * yield `250`. The real defects were NULLABILITY and PRECISION — `parseInt`
     * truncated a 250,50 € ceiling to 250.
     *
     * SO `decimal()` IS A DELIBERATE BEHAVIOUR CHANGE: `PgNumeric` returns the
     * string verbatim, so reads now yield `'250'`. Consumers already coerce
     * (`_mapMaxAmountWithoutQuoteHt`, `score-quote.ts`). Writes send a number
     * through the DTO and the API stringifies it (#2481).
     *
     * NOT narrowed the other way: `ALTER TYPE integer` + `SET NOT NULL` is not
     * expand-safe against a running release, and NULL is a MEANINGFUL third value
     * — `null` is « no ceiling », `0` is « Manuel / devis obligatoire », and the
     * technician app reads the two differently.
     */
    interventionTriggerLimit: decimal('intervention_trigger_limit').default('250'),
    /**
     * Permission to be decided for (#1586 ruling 9). Separate from
     * {@link organizations.quoteAutoDecisionMode} on purpose: a threshold that
     * silently gained the power to sign contracts is not informed consent, so
     * the amount and the permission are two writes. Was the client's own
     * consent; since #3049 it is a Depanneo contract term (SUPER_ADMIN-only in
     * the API, read-only for the client).
     */
    quoteAutoValidationOptIn: boolean('quote_auto_validation_opt_in')
        .default(false)
        .notNull(),
    /**
     * How far Depanneo lets the engine act for this org — the rollout ladder,
     * SUPER_ADMIN-only (and a Postgres invariant, see this migration's guard).
     * Never read on its own: without the opt-in above this rung is CAPPED at
     * `auto_share` — not collapsed to `off` — and the global kill switch
     * overrides both. `resolveQuoteAutoDecision` is the one place that combines
     * them; a call site re-deriving `optIn ? mode : off` gets it wrong.
     */
    quoteAutoDecisionMode: text('quote_auto_decision_mode')
        .$type()
        .default(QuoteAutoDecisionMode.OFF)
        .notNull(),
    // When true, the request-creation form pre-checks "Je souhaite recevoir un
    // devis" so every new request defaults to the quote path. One of two
    // independent OR gates onto the quote path (the other is the trigger limit).
    createQuoteByDefault: boolean('create_quote_by_default')
        .default(false)
        .notNull(),
    paymentDelay: integer('payment_delay').default(30).notNull(),
    /**
     * A global percentage discount every STANDARD / DOWN_PAYMENT invoice of this
     * org must carry, printed under `mandatoryDiscountLabel` (#2672 — the
     * Basic-Fit 3 % of the ARESS org, « Remise 3% Basic Fit »). Both null = no
     * such discount; set together or not at all
     * (`organizations_mandatory_discount_check`). SUPER_ADMIN-only, in app code
     * and in Postgres (`guard_organization_mandatory_discount`, migration 0194).
     * The invoices domain applies and locks it; avoirs and commissions are exempt.
     */
    mandatoryDiscountPercent: decimal('mandatory_discount_percent', {
        precision: 5,
        scale: 2,
    }),
    mandatoryDiscountLabel: text('mandatory_discount_label'),
    // Default settings
    timezone: text('timezone').default('Europe/Paris'),
    locale: text('locale').default('fr-FR'),
    logoDocumentId: uuid('logo_document_id').references(() => documents.id),
    // Audit fields
    ...auditTimestamps,
    notes: text('notes'),
    onboardingCompletedAt: timestamp('onboarding_completed_at', {
        withTimezone: true,
    }),
    onboardingStep: integer('onboarding_step').notNull().default(0),
    // ── Billing address (may differ from legal address) ──────────────────────────
    // billingSameAsLegal = true (default): billing = org legal address.
    // billingSameAsLegal = false: the fields below override the billing identity.
    // This supports French businesses that invoice under a different entity
    // (e.g., a parent company, franchise group, or holding structure).
    billingSameAsLegal: boolean('billing_same_as_legal')
        .notNull()
        .default(true),
    // Name of the billing entity when different from the org's legal name.
    // Maps to "Société / enseigne d'appartenance" in the Figma billing step.
    billingLegalName: text('billing_legal_name'),
    billingAddressLine1: text('billing_address_line1'),
    billingAddressLine2: text('billing_address_line2'),
    billingCity: text('billing_city'),
    billingPostalCode: text('billing_postal_code'),
    billingCountry: text('billing_country'),
    billingGmapsPlaceId: text('billing_gmaps_place_id'),
    // Billing-specific SIREN and VAT — distinct from the org's own siren/vatNumber.
    // Only populated when billingSameAsLegal = false.
    billingSiren: text('billing_siren'),
    billingVatNumber: text('billing_vat_number'),
    // ── Shared billing entities (#1444) ─────────────────────────────────────────
    // The N legal identities this org invoices under, keyed by `BillingEntity.key`;
    // `sites.billing_entity_key` points at one of them and N sites may share one.
    // Read by `resolveBilledClient` only, and only when
    // `billingEntityTypes[0] !== 'organization'`. See the `BillingEntity` doc
    // comment for why this is jsonb and when to promote it to a table.
    //
    // jsonb has no DB-level shape guarantee, so `validateBillingEntities`
    // (`@depanneo/validation`) is the ONLY guard — every write path runs it.
    billingEntities: jsonb('billing_entities')
        .$type()
        .notNull()
        .default(sql `'[]'::jsonb`),
    // ── Services (#2665) ────────────────────────────────────────────────────────
    // The « Service » list an intervention picks from — empty for every org but
    // Verisure, and empty means nothing changes anywhere. SUPER_ADMIN-only write;
    // `validateOrganizationServices` (`@depanneo/validation`) is its only shape
    // guard. See docs/decisions/0003-intervention-service.md.
    services: jsonb('services')
        .$type()
        .notNull()
        .default(sql `'[]'::jsonb`),
    // ── Invoice contact additional phone ─────────────────────────────────────────
    // invoiceContactPhone (existing) stores the mobile phone.
    // invoiceContactLandline stores the optional fixed line ("Téléphone fixe").
    invoiceContactLandline: text('invoice_contact_landline'),
}, table => ({
    // At most ONE platform organization, enforced by Postgres. Partial unique
    // index (same shape as `idx_contacts_primary` on technician_contacts) so
    // the `false` rows — every client tenant — are not part of the constraint.
    //
    // `deletedAt IS NULL` is load-bearing, not defensive: soft delete is the only
    // delete here (root rule 4), so without it a soft-deleted platform org keeps
    // occupying the singleton slot forever and no live org can ever be marked
    // again — `_resolvePlatformOrganizationId` filters `deletedAt` out, so it
    // would report "not configured" while the index refuses the repair.
    platformOrgIdx: uniqueIndex('idx_organizations_platform')
        .on(table.isPlatform)
        .where(sql `${table.isPlatform} = true AND ${table.deletedAt} IS NULL`),
    // A percent with no label prints a nameless line, a label with no percent
    // discounts nothing, and 0 % or 100 % is no discount or a free invoice.
    // The explicit NOT NULLs are load-bearing: with one half NULL the comparisons
    // yield NULL, and a CHECK that evaluates to NULL passes.
    mandatoryDiscountCheck: check('organizations_mandatory_discount_check', sql `
        (${table.mandatoryDiscountPercent} IS NULL AND ${table.mandatoryDiscountLabel} IS NULL)
        OR (
          ${table.mandatoryDiscountPercent} IS NOT NULL
          AND ${table.mandatoryDiscountLabel} IS NOT NULL
          AND ${table.mandatoryDiscountPercent} > 0
          AND ${table.mandatoryDiscountPercent} < 100
          AND btrim(${table.mandatoryDiscountLabel}) <> ''
        )
      `),
}));
export const sites = pgTable('sites', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    name: text('name').notNull(),
    status: varchar('status', { length: 50 }).$type().notNull(),
    code: text('code'),
    tags: text('tags').array(),
    type: varchar('type', { length: 50 }).$type().notNull(),
    businessModel: varchar('business_model', { length: 50 })
        .$type()
        .notNull()
        .default(BusinessModels.B2B),
    // OPERATIONAL ADDRESS
    // Written ONLY by SiteAddressService.resolveOperationalAddress (#1443):
    // address_line1 + street-exact coordinates always land in the same write.
    addressLine1: text('address_line1').notNull(),
    addressLine2: text('address_line2'),
    city: text('city'),
    postalCode: text('postal_code'),
    department: text('department'),
    region: text('region'),
    country: text('country').notNull().default('FR'),
    // Deliberately NO { precision, scale }: unlike `technicians`, these stay
    // unconstrained `numeric`. The range checks below are what enforce the
    // invariant; narrowing the type is mere parity and would force a DROP +
    // CREATE of the `fact_intervention` view, which reads both columns (#1443).
    latitude: decimal('latitude').notNull(),
    longitude: decimal('longitude').notNull(),
    gmapsPlaceId: text('gmaps_place_id'),
    // Google's `geometry.location_type` for the coordinates above, recorded for
    // post-hoc audit and NEVER branched on (#1443). Nullable: rows written
    // before the geocoding funnel existed have none.
    locationType: text('location_type'),
    //LEGAL ADDRESS
    // Misnamed, deliberately NOT renamed (#1445): it selects the whole billed
    // IDENTITY for this site — `legalName`, `siren`, `vatNumber` AND address —
    // not merely an address. `organization` bills the org (a legitimate per-site
    // opt-out), `site` bills the site's operational address, `custom` bills the
    // site's own billing block below. Read only when
    // `organizations.billingEntityTypes[0] !== 'organization'`; the one reader is
    // `resolveBilledClient`
    // (apps/api/src/domains/billing/billed-client/billed-client.resolver.ts).
    // The column name is the on-the-wire JSON key into `CreateSiteDto` and
    // `main.ts` sets `forbidNonWhitelisted: true`, so renaming it 400s every
    // old web client during a deploy — 3 releases for zero behaviour change.
    // User-facing / CSV vocabulary: `entite_facturation`.
    legalAddressSource: legalAddressSourceEnum('legal_address_source')
        .notNull()
        .default(AddressSource.ORGANIZATION),
    // Points at one `BillingEntity.key` in `organizations.billing_entities`
    // (#1444) — the SHARED identity mechanism: 100 sites of one client carry the
    // same key and bill under one entity. Nullable and explicit: NULL means « no
    // shared entity », and the resolution order is fixed (owner-locked):
    //   1. key set   -> that entity
    //   2. key NULL  -> `legalAddressSource` above decides
    // The two mechanisms COEXIST; neither is a migration of the other. Not an FK
    // (jsonb cannot be an FK target), so the guard is application-side:
    // `OrganizationsService` refuses to drop a key a live site still references,
    // and both the site form and the CSV importer hard-fail an unmatched key.
    // No index: ~300 rows per org, and `sites` is hot enough that an index here
    // would need out-of-band `CREATE INDEX CONCURRENTLY` for nothing.
    billingEntityKey: text('billing_entity_key'),
    legalAddressLine1: text('legal_address_line1'),
    legalAddressLine2: text('legal_address_line2'),
    legalCity: text('legal_city'),
    legalPostalCode: text('legal_postal_code'),
    legalDepartment: text('legal_department'),
    legalRegion: text('legal_region'),
    legalCountry: text('legal_country').default('FR'),
    legalGmapsPlaceId: text('legal_gmaps_place_id'),
    // LEGAL BILLING INFO
    // NOT unique, and must not become unique again (#1488). A legal identifier is
    // shared by design: 100 sites of one client that bill under one entity carry
    // the same SIREN and VAT number, so the global UNIQUE constraints these three
    // columns used to carry made that import impossible — and leaked, across
    // tenants, whether a given SIREN was already registered. Dropped by
    // `0159_drop_site_legal_identifier_uniques.sql`; format validation
    // (`@depanneo/validation`) is the guard that remains.
    siren: text('siren'),
    siret: text('siret'),
    vatNumber: text('vat_number'),
    legalName: text('legal_name'),
    billingInstructions: text('billing_instructions'),
    // INVOICE
    invoiceContactType: varchar('invoice_contact_type', { length: 50 })
        .$type()
        .notNull()
        .default(InvoiceContactType.EXTERNAL),
    invoiceUserId: uuid('invoice_user_id').references(() => users.id),
    invoiceContactName: text('invoice_contact_name'),
    invoiceContactEmail: text('invoice_contact_email'),
    invoiceContactPhone: text('invoice_contact_phone'),
    // B2B2C RULES
    allowExternalContactsOnly: boolean('allow_external_contacts_only').default(false),
    // Read by NO code (#1445, grep-verified): it does NOT control who is billed.
    // That is `organizations.billingEntityTypes[0]` (who pays) plus
    // `billingEntityKey` / `legalAddressSource` above (which identity). #1444
    // shipped the shared-entity mechanism on its OWN column and deliberately gave
    // this one no meaning — dead configuration, do not start branching on it.
    // Retiring it needs the `fact_intervention` view rebuilt first (it aliases
    // this column as `billing_entity_type`), so it is a two-release contract of
    // its own.
    billingEntity: varchar('billing_entity', { length: 50 })
        .$type()
        .notNull()
        .default(BillingEntityType.ORGANIZATION),
    // OPERATIONAL SETTINGS
    // IANA zone this site's wall-clock is read in (#1859). NULL means « inherit
    // the organization's », so there is no backfill and no default here — the
    // fallback lives in `resolveTimezone`, the single reader.
    timezone: text('timezone'),
    operatingHours: json('operating_hours'),
    holidaySchedule: json('holiday_schedule'),
    accessInstructions: text('access_instructions'),
    accessConstraints: text('access_constraints'),
    customFields: json('custom_fields'),
    // AUDIT FIELDS
    ...auditTimestamps,
}, table => ({
    // Analytics geo dimension (#1265): region filter/group-by.
    regionIdx: index('sites_region_idx').on(table.region),
    latitudeRangeCheck: check('check_latitude_range', sql `${table.latitude} >= -90 AND ${table.latitude} <= 90`),
    longitudeRangeCheck: check('check_longitude_range', sql `${table.longitude} >= -180 AND ${table.longitude} <= 180`),
    // (0,0) is a real point in the Gulf of Guinea, so it passes both range
    // checks and every "coordinates exist" NULL test while misrouting dispatch
    // silently. It is the value every broken web form defaulted to (#1443), so
    // it gets its own constraint.
    coordinatesNotNullIslandCheck: check('check_coordinates_not_null_island', sql `NOT (${table.latitude} = 0 AND ${table.longitude} = 0)`),
}));
export const users = pgTable('users', {
    id: uuid('id').primaryKey().defaultRandom(),
    supabaseId: uuid('supabase_id')
        .unique()
        .references(() => authUsers.id),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    email: text('email').notNull().unique(),
    firstName: text('first_name').notNull(),
    lastName: text('last_name').notNull(),
    jobTitle: text('job_title'),
    avatarDocumentId: uuid('avatar_document_id').references(() => documents.id),
    // Role & Permissions
    role: userRoleEnum('role').notNull().default(UserRole.VIEWER),
    status: userStatusEnum('status').notNull().default(UserStatus.ACTIVE),
    permissions: json('permissions').default({}),
    canAccessAllSites: boolean('can_access_all_sites').default(false),
    // Contact Info
    mobilePhone: text('mobile_phone'),
    landlinePhone: text('landline_phone'),
    // Preferences
    locale: text('locale').default('fr-FR'),
    emailNotifications: boolean('email_notifications').default(true),
    pushNotifications: boolean('push_notifications').default(false),
    smsNotifications: boolean('sms_notifications').default(false),
    preferences: json('preferences').$type(),
    // Metadata
    notes: text('notes'),
    tags: text('tags').array(),
    // Tracking
    passwordChangedAt: timestamp('password_changed_at', { withTimezone: true }),
    // Audit fields
    ...auditTimestamps,
});
export const equipment = pgTable('equipment', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // Equipment Details
    name: text('name').notNull(),
    brand: text('brand'),
    model: text('model'),
    category: text('category'),
    subCategory: text('sub_category'),
    jobType: varchar('job_type', { length: 50 }).$type().notNull(),
    // Catalog Item Flag
    isCatalogItem: boolean('is_catalog_item').default(false).notNull(),
    // Catalog profile provenance (#2761), written only by
    // `importCatalogProfileIntoOrgInTx`: the stable key a re-import matches on,
    // and the one « Autres travaux de … » entry per trade the V1 importer uses.
    catalogProfile: varchar('catalog_profile', {
        length: 10,
    }).$type(),
    catalogKey: text('catalog_key'),
    isGeneric: boolean('is_generic').default(false).notNull(),
    // The `OrganizationService.code` this row is offered under (#2665). NULL in an
    // org without services, never NULL in one with services.
    serviceCode: varchar('service_code', { length: 50 }),
    // Maintenance Configuration
    maintenanceType: varchar('maintenance_type', { length: 50 })
        .$type()
        .default(MaintenanceType.PREVENTIVE_CURATIVE)
        .notNull(),
    maintenanceFrequency: text('maintenance_frequency'),
    maintenanceInstructions: text('maintenance_instructions'),
    isActive: boolean('is_active').default(true).notNull(),
    // Audit Fields
    ...auditTimestamps,
}, table => ({
    // One live row per source key per org: a re-import updates in place instead
    // of duplicating. `deletedAt IS NULL` so a retired row frees its key.
    catalogKeyIdx: uniqueIndex('idx_equipment_catalog_key')
        .on(table.organizationId, table.catalogKey)
        .where(sql `${table.catalogKey} IS NOT NULL AND ${table.deletedAt} IS NULL`),
    // At most one live generic entry per trade per SERVICE per org (#2665) — what
    // makes the V1 importer's trade → generic lookup unambiguous, while Verisure
    // holds a Sinistre and a Serrure locksmith generic. `COALESCE`, not
    // `NULLS NOT DISTINCT`, so an org without services keeps the meaning of the
    // per-(org, trade) index it replaced (dropped by 0198).
    genericPerServiceTradeIdx: uniqueIndex('idx_equipment_generic_per_service_trade')
        .on(table.organizationId, sql `COALESCE(${table.serviceCode}, '')`, table.jobType)
        .where(sql `${table.isGeneric} = true AND ${table.deletedAt} IS NULL`),
}));
export const equipmentSites = pgTable('equipment_sites', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    equipmentId: uuid('equipment_id')
        .notNull()
        .references(() => equipment.id),
    siteId: uuid('site_id')
        .notNull()
        .references(() => sites.id),
    // Site-Specific Properties
    serialNumber: text('serial_number').unique(),
    manufacturingYear: integer('manufacturing_year'),
    purchasePrice: decimal('purchase_price', { precision: 10, scale: 2 }),
    purchaseCurrency: text('purchase_currency').default('EUR').notNull(),
    installationDate: date('installation_date'),
    warrantyStartDate: date('warranty_start_date'),
    warrantyEndDate: date('warranty_end_date'),
    warrantyDurationMonths: integer('warranty_duration_months'),
    // Maintenance Tracking
    lastMaintenanceDate: date('last_maintenance_date'),
    nextMaintenanceDate: date('next_maintenance_date'),
    maintenanceNotes: text('maintenance_notes'),
    // Operational Status
    condition: varchar('condition', { length: 50 })
        .$type()
        .default(EquipmentCondition.GOOD)
        .notNull(),
    operability: varchar('operability', { length: 50 })
        .$type()
        .default(EquipmentOperability.FUNCTIONAL)
        .notNull(),
    criticality: varchar('criticality', { length: 50 })
        .$type()
        .default(EquipmentCriticality.MEDIUM)
        .notNull(),
    status: varchar('status', { length: 50 })
        .$type()
        .default(EquipmentStatus.ACTIVE)
        .notNull(),
    // Location Details
    locationDetails: text('location_details'),
    accessInstructions: text('access_instructions'),
    // Audit Fields
    ...auditTimestamps,
});
export const siteContacts = pgTable('site_contacts', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    siteId: uuid('site_id')
        .notNull()
        .references(() => sites.id),
    // Contact Source
    contactType: varchar('contact_type', { length: 50 })
        .$type()
        .notNull()
        .default(SiteContactType.USER),
    userId: uuid('user_id').references(() => users.id), // Foreign key to internal users table, NULL if external contact
    contactFirstName: text('contact_first_name'), // External contact firstName, NULL if user (auto-filled from user table)
    contactLastName: text('contact_last_name'), // External contact lastName, NULL if user (auto-filled from user table)
    contactEmail: text('contact_email'), // Contact email, NULL if user (auto-filled from user table)
    contactLandline: text('contact_landline'),
    contactMobile: text('contact_mobile'),
    contactTitle: text('contact_title'),
    // Contact Role and Flags
    isPrimary: boolean('is_primary').default(false).notNull(),
    isNotificationEnabled: boolean('is_notification_enabled')
        .default(true)
        .notNull(),
    notes: text('notes'),
    isActive: boolean('is_active').default(true).notNull(),
    // Audit Fields
    ...auditTimestamps,
});
export const documents = pgTable('documents', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // File Details
    originalFilename: text('original_filename').notNull(),
    storageFilename: text('storage_filename').notNull(),
    fileUrl: text('file_url').notNull(),
    thumbnailUrl: text('thumbnail_url'),
    fileSizeBytes: bigint('file_size_bytes', { mode: 'number' }),
    mimeType: text('mime_type'),
    fileHash: text('file_hash'),
    // Document Categorization and Metadata
    documentType: varchar('document_type', { length: 50 })
        .$type()
        .default(DocumentType.OTHER)
        .notNull(),
    title: text('title').notNull(),
    description: text('description'),
    tags: text('tags').array(),
    // Access and Versioning
    isPublic: boolean('is_public').default(false).notNull(),
    versionNumber: integer('version_number').default(1).notNull(),
    parentDocumentId: uuid('parent_document_id').references(() => documents.id),
    isActive: boolean('is_active').default(true).notNull(),
    // Audit Fields
    ...auditTimestamps,
    ...technicianAuditTimestamps,
}, t => [unique('documents_file_hash_org_key').on(t.fileHash, t.organizationId)]);
export const entityDocuments = pgTable('entity_documents', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // Document Reference
    documentId: uuid('document_id')
        .notNull()
        .references(() => documents.id),
    // Polymorphic Entity Link
    entityType: entityTypeEnum('entity_type').notNull(),
    entityId: uuid('entity_id').notNull(), // Polymorphic FK - references different tables based on entity_type
    // Note: Drizzle does not support direct polymorphic FK constraints.
    // Validation for this relationship will be handled at the application level.
    // Document Purpose and Flags
    documentRole: text('document_role').default('general').notNull(),
    isRequired: boolean('is_required').default(false).notNull(),
    isPrimary: boolean('is_primary').default(false).notNull(),
    displayOrder: integer('display_order').default(0).notNull(),
    // Access Control
    accessLevel: documentAccessLevelEnum('access_level')
        .default(DocumentAccessLevel.INTERNAL)
        .notNull(),
    // Notes
    notes: text('notes'),
    // Audit Fields
    ...auditTimestamps,
    ...technicianAuditTimestamps,
});
/**
 * Durable crash-recovery record for a Storage object about to be written
 * (#1417 Phase 1). Written BEFORE the Storage upload so a process death
 * between the upload and the DB attach tx leaves a durable trace of the
 * orphaned object; `StorageCleanupReaper` (documents domain) sweeps it after
 * a grace window.
 *
 * System table, not tenant data: no tenant RLS policies (see
 * `rls-classification.ts` NO_RLS_SERVICE_ROLE) — accessed only via
 * `drizzleService.admin`. `organizationId` is audit-only, no FK: the storage
 * path itself is the row's identity and the reaper never filters by org.
 */
export const storageCleanupIntents = pgTable('storage_cleanup_intents', {
    id: uuid('id').primaryKey().defaultRandom(),
    bucket: text('bucket').notNull(),
    storagePath: text('storage_path').notNull(),
    organizationId: uuid('organization_id'),
    attempts: integer('attempts').default(0).notNull(),
    lastError: text('last_error'),
    // Set atomically together with `attempts` when the reaper claims an
    // intent for processing (R1 FIX-5) — the claim UPDATE's WHERE clause
    // requires this to be null or stale, so two reaper runs (same instance
    // overlapping, or two Railway replicas) can never process the same
    // intent concurrently.
    lastAttemptAt: timestamp('last_attempt_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, table => [
    index('storage_cleanup_intents_created_at_idx').on(table.createdAt),
]);
export const notes = pgTable('notes', {
    // Core Identification
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // Polymorphic Entity Reference
    entityType: entityTypeEnum('entity_type').notNull(),
    entityId: uuid('entity_id').notNull(),
    // Note Content
    content: text('content').notNull(),
    authorId: uuid('author_id').references(() => users.id),
    authorTechnicianId: uuid('author_technician_id').references(() => technicians.id),
    // Reply Hierarchy (parent_id = NULL for main notes, has value for replies)
    parentId: uuid('parent_id'),
    isReply: boolean('is_reply').default(false).notNull(),
    // Read tier. Enforced by notes_select_rls (migration 0173); a reply always
    // copies its parent's value.
    audience: noteAudienceEnum('audience').notNull().default(NoteAudience.ALL),
    // Audit Fields
    createdAt: timestamp('created_at', { withTimezone: true }).defaultNow(),
    createdBy: uuid('created_by').references(() => users.id),
    updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow(),
    updatedBy: uuid('updated_by').references(() => users.id),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    deletedBy: uuid('deleted_by').references(() => users.id),
}, table => ({
    entityIdx: index('notes_entity_idx').on(table.entityType, table.entityId),
    entityIdIdx: index('notes_entity_id_idx').on(table.entityId),
    organizationIdx: index('notes_organization_idx').on(table.organizationId),
    authorIdx: index('notes_author_idx').on(table.authorId),
    parentIdx: index('notes_parent_idx').on(table.parentId),
    // Composite indexes for findByEntityWithAdmin query pattern
    authorTechnicianIdx: index('notes_author_technician_idx').on(table.authorTechnicianId, table.organizationId),
    authorOrgIdx: index('notes_author_id_org_idx').on(table.authorId, table.organizationId),
    // Composite index for multi-tenant entity queries
    entityOrgIdx: index('notes_entity_org_idx').on(table.entityType, table.entityId, table.organizationId),
    // Ensure isReply is always consistent with parentId
    isReplyConsistent: check('is_reply_consistent', sql `is_reply = (parent_id IS NOT NULL)`),
    // Ensure exactly one author type is set (user OR technician, not both)
    authorCheck: check('notes_author_check', sql `(author_id IS NOT NULL AND author_technician_id IS NULL) OR (author_id IS NULL AND author_technician_id IS NOT NULL)`),
}));
export const noteRecipients = pgTable('note_recipients', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    noteId: uuid('note_id')
        .notNull()
        .references(() => notes.id),
    userId: uuid('user_id')
        .notNull()
        .references(() => users.id),
}, table => ({
    noteIdx: index('note_recipients_note_idx').on(table.noteId),
    userIdx: index('note_recipients_user_idx').on(table.userId),
    organizationIdx: index('note_recipients_organization_idx').on(table.organizationId),
    uniqueRecipient: uniqueIndex('note_recipient_unique_idx').on(table.noteId, table.userId),
}));
export const interventions = pgTable('interventions', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    externalId: varchar('external_id', { length: 100 }),
    // Intervention Classification
    interventionType: interventionTypeEnum('intervention_type').notNull(),
    priority: priorityLevelEnum('priority')
        .notNull()
        .default(PriorityLevel.NORMAL),
    status: varchar('status', { length: 50 })
        .$type()
        .notNull()
        .default(InterventionStatus.DRAFT),
    title: text('title').notNull(),
    description: text('description').notNull(),
    // The org's `OrganizationService.code` (#2665) — a stable code, never the
    // label. No default, no CHECK, no FK, and no index yet: `interventions` is
    // hot, so a later one is built out-of-band `CONCURRENTLY`.
    serviceCode: varchar('service_code', { length: 50 }),
    // Site Relationship
    siteId: uuid('site_id').references(() => sites.id),
    isTemporarySite: boolean('is_temporary_site').notNull().default(false),
    temporarySiteData: jsonb('temporary_site_data').$type(),
    // Equipment Relationship
    equipmentId: uuid('equipment_id').references(() => equipment.id),
    equipmentType: varchar('equipment_type', { length: 100 }),
    // Technician Relationship
    technicianId: uuid('technician_id').references(() => technicians.id),
    assignedBy: uuid('assigned_by').references(() => users.id),
    assignedAt: timestamp('assigned_at', { withTimezone: true }),
    commissionPercentage: decimal('commission_percentage', {
        precision: 5,
        scale: 2,
    }),
    // ON DELETE SET NULL: this is a snapshot/provenance link (#1211). The
    // nightly technician sync replaces work zones (fresh UUIDs), so the DB nulls
    // this link when the referenced zone is deleted — the frozen
    // `commission_percentage` above is the source of truth for billing; the
    // zone-id is only provenance. (Avoids a cross-tenant admin write in the sync.)
    commissionWorkZoneId: uuid('commission_work_zone_id').references(() => technicianWorkZones.id, { onDelete: 'set null' }),
    // Which resolution level produced `commission_percentage` (#1588). Nullable
    // on purpose: rows frozen before the resolver became total (and the 26 rates
    // backfilled in production on 2026-08-25) carry a rate whose level cannot be
    // reconstructed safely, and guessing one would be worse than saying nothing.
    commissionRateSource: varchar('commission_rate_source', {
        length: 30,
    }).$type(),
    requestedAt: timestamp('requested_at', { withTimezone: true }),
    scheduledAt: timestamp('scheduled_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    technicianArrivalTime: timestamp('technician_arrival_time', {
        withTimezone: true,
    }),
    // #3062: the arrival a contract price (#2670) is computed on, set by a
    // SUPER_ADMIN when the technician's is wrong or missing. Kept apart from
    // `technicianArrivalTime` and the signed report's `arrivalTime` on purpose:
    // neither stamp is rewritten, and this one is never printed.
    billedArrivalAt: timestamp('billed_arrival_at', { withTimezone: true }),
    // Business Logic
    quoteRequested: boolean('quote_requested').notNull().default(false),
    // Status qualifiers for AWAITING_QUOTE (technician devis request), same
    // precedent as onHoldReason/onHoldNote for ON_HOLD.
    quoteRequestType: varchar('quote_request_type', {
        length: 50,
    }).$type(),
    quoteRequestEstimatedAmount: decimal('quote_request_estimated_amount', {
        precision: 10,
        scale: 2,
    }),
    // Per-org sequential human-readable reference (e.g. INT-00042).
    // Assigned atomically at creation via intervention_sequences; nullable
    // only for legacy rows created before the backfill migration.
    reference: varchar('reference', { length: 20 }),
    internalReference: varchar('internal_reference', { length: 100 }),
    internalNote: text('internal_note'),
    // Contact Information (Value Objects)
    siteContact: jsonb('site_contact').$type(),
    billingContact: jsonb('billing_contact').$type(),
    billingSameAsSite: boolean('billing_same_as_site').notNull().default(true),
    // Users
    assignedUserIds: uuid('assigned_user_ids').array().notNull().default([]),
    onHoldReason: varchar('on_hold_reason', {
        length: 50,
    }).$type(),
    onHoldNote: text('on_hold_note'),
    // #2180: what « Reprendre » restores. NULL on rows held before it existed.
    statusBeforeHold: varchar('status_before_hold', {
        length: 50,
    }).$type(),
    // Dispatch-failed flag layered on AWAITING_ASSIGNMENT (#1205): set when a
    // dispatch attempt finds no technician (zero eligible matches, or all waves
    // exhausted). NOT a status — recomputed on every dispatch attempt and only
    // meaningful while status === AWAITING_ASSIGNMENT. null = no failed attempt.
    dispatchFailedAt: timestamp('dispatch_failed_at', { withTimezone: true }),
    dispatchFailureReason: varchar('dispatch_failure_reason', {
        length: 50,
    }).$type(),
    // Orphan-dispatch sweep lease (#1684). The recovery cron looks for an
    // AWAITING_ASSIGNMENT intervention with ZERO live dispatch rows — a state no
    // other cron notices — and re-runs matching for it. Stamping this column in
    // the same conditional UPDATE that selects the row IS the multi-replica
    // claim: two sweeps racing the same orphan produce exactly one dispatch
    // attempt. It doubles as the re-attempt throttle, because an orphan the
    // matcher still cannot fill stays an orphan and would otherwise be swept
    // (and Sentry-alerted for NO_MATCH) on every tick — see
    // `ORPHAN_SWEEP_LEASE_INTERVAL`. NULL = never swept.
    dispatchSweepLeaseAt: timestamp('dispatch_sweep_lease_at', {
        withTimezone: true,
    }),
    hasDispute: boolean('has_dispute').notNull().default(false),
    disputeReportedAt: timestamp('dispute_reported_at', { withTimezone: true }),
    disputeResolvedAt: timestamp('dispute_resolved_at', { withTimezone: true }),
    disputeCategory: varchar('dispute_category', {
        length: 50,
    }).$type(),
    disputeReason: text('dispute_reason'),
    visitNumber: integer('visit_number').notNull().default(1),
    // Mailbox ordering key (#1552): when this intervention last moved. Stamped by
    // `ActivityLoggerListener._logActivity` on the same events it writes to
    // `activity_logs` — denormalised on purpose, because cursor pagination cannot
    // key off a `MAX(created_at) GROUP BY entity_id` subquery through an index.
    // NOT NULL with a default so the list query needs no COALESCE. Migration 0164
    // deliberately does NOT backfill from `created_at`: an UPDATE on this table
    // fires the `updated_at` and search-sync row triggers, so pre-existing rows
    // share the deploy timestamp and are tie-broken by `id` until their first
    // activity lifts them. The optional batched seeding is documented there.
    //
    // `precision: 3` IS LOAD-BEARING, not tidiness. This column is a keyset
    // pagination key whose cursor carries its value as an ISO string, and a JS
    // `Date` (what node-postgres hands back) holds only milliseconds. At the
    // default microsecond precision the cursor value is a TRUNCATION of the
    // stored one, so `> cursor` re-serves the anchor row itself (a duplicate) and
    // `< cursor` skips every row inside that same millisecond. Pinning the column
    // to milliseconds makes the round-trip lossless instead.
    lastActivityAt: timestamp('last_activity_at', {
        withTimezone: true,
        precision: 3,
    })
        .notNull()
        .defaultNow(),
    // Audit Fields
    ...auditTimestamps,
}, table => ({
    // Composite indexes for cursor pagination
    createdAtIdx: index('interventions_created_at_idx').on(table.organizationId, table.deletedAt, table.createdAt, table.id),
    priorityIdx: index('interventions_priority_idx').on(table.organizationId, table.deletedAt, table.priority, table.id),
    requestedAtIdx: index('interventions_requested_at_idx').on(table.organizationId, table.deletedAt, table.requestedAt, table.id),
    scheduledAtIdx: index('interventions_scheduled_at_idx').on(table.organizationId, table.deletedAt, table.scheduledAt, table.id),
    // Analytics fact surface (#1265): completed_at range scans per org.
    completedAtIdx: index('interventions_completed_at_idx').on(table.organizationId, table.deletedAt, table.completedAt),
    statusIdx: index('interventions_status_idx').on(table.organizationId, table.deletedAt, table.status, table.id),
    // Default list sort (#1552). Same shape as the five above so a backward index
    // scan serves `ORDER BY last_activity_at DESC, id DESC` outright.
    lastActivityAtIdx: index('interventions_last_activity_at_idx').on(table.organizationId, table.deletedAt, table.lastActivityAt, table.id),
    // Filter indexes with cursor pagination support
    siteIdx: index('interventions_site_idx').on(table.organizationId, table.deletedAt, table.siteId, table.id),
    equipmentIdx: index('interventions_equipment_idx').on(table.organizationId, table.deletedAt, table.equipmentId, table.id),
    typeIdx: index('interventions_type_idx').on(table.organizationId, table.deletedAt, table.interventionType, table.id),
    createdByIdx: index('interventions_created_by_idx').on(table.organizationId, table.deletedAt, table.createdBy, table.id),
    technicianIdx: index('interventions_technician_idx').on(table.organizationId, table.deletedAt, table.technicianId, table.id),
    assignedByIdx: index('interventions_assigned_by_idx').on(table.organizationId, table.deletedAt, table.assignedBy, table.id),
    // FK REFERENCING-COLUMN index, not a tenant-query index — deliberately NOT
    // the `(organizationId, deletedAt, col, id)` shape every index above uses.
    // `commissionWorkZoneId` references `technician_work_zones.id` ON DELETE SET
    // NULL (migrations 0124 + 0133), and Postgres does not index a referencing
    // column automatically. On every delete of a work zone the RI action runs
    // `UPDATE ONLY interventions SET commission_work_zone_id = NULL WHERE
    // commission_work_zone_id = $1` — keyed on this column ALONE, so an index
    // leading with `organizationId` cannot serve it and the update degrades to a
    // sequential scan of `interventions` PER DELETED ROW. The nightly technician
    // sync deletes every work zone (`bulkLoadTechnicians` step 3), so that cost
    // is multiplied by the whole zone set; it is also paid by any single
    // work-zone delete today, which is why this index is worth having
    // independently of #1417.
    // Single-column and NOT partial on `deletedAt`: the RI action must find
    // referencing rows regardless of Harmony's app-level soft delete, so a
    // partial index would leave soft-deleted rows on the seq-scan path.
    commissionWorkZoneIdx: index('interventions_commission_work_zone_idx').on(table.commissionWorkZoneId),
    // Covering index for stats queries (no id needed for aggregations)
    statsIdx: index('interventions_stats_idx').on(table.organizationId, table.deletedAt, table.priority, table.status),
    // Per-org human-readable reference must be unique within an organization
    orgReferenceUnique: uniqueIndex('interventions_org_reference_unique').on(table.organizationId, table.reference),
    // Soft-deleted rows count (#2813): a deleted ticket must never be re-created.
    orgExternalIdUnique: uniqueIndex('interventions_org_external_id_unique')
        .on(table.organizationId, table.externalId)
        .where(sql `${table.externalId} IS NOT NULL`),
}));
/**
 * Per-user read state for the mailbox list (#1552). One row per (user,
 * intervention), written when the user opens the intervention. Unread =
 * `interventions.last_activity_at > last_read_at`, or no row at all.
 *
 * Deliberately NOT derived from `user_notifications`: retention hard-deletes
 * those at 30 days and `NOTIFICATION_ROUTING` excludes some roles from some
 * events (an ADMIN gets no note notifications), so a derived unread list would
 * have holes nobody could explain.
 *
 * RLS is `user_id = get_current_user_id()` on select/insert/update — see
 * migration `0166_intervention_activity_and_reads.sql` (policies live only
 * there, same as `user_notifications` / migration 0082). No DELETE policy or
 * grant: nothing deletes a read row (`COMMAND_EXEMPTIONS` in
 * `apps/api/src/drizzle/rls-classification.ts`). No soft delete either, for the
 * same reason.
 */
export const interventionReads = pgTable('intervention_reads', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    userId: uuid('user_id')
        .notNull()
        .references(() => users.id),
    interventionId: uuid('intervention_id')
        .notNull()
        .references(() => interventions.id),
    lastReadAt: timestamp('last_read_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, table => ({
    // The upsert target, and the lookup the list uses (one row per user+row).
    userInterventionUnique: uniqueIndex('intervention_reads_user_intervention_unique').on(table.userId, table.interventionId),
    interventionIdx: index('intervention_reads_intervention_idx').on(table.interventionId),
}));
/**
 * Conformed analytics fact surface (#1265, foundation F1). One row per active
 * intervention, joined to site geo, equipment métier, manager, business model,
 * and an invoice cost rollup. Every dashboard metric reads from this surface.

 *
 * NEUTRAL DATA LAYER (no RLS): the view returns ALL companies' data for every
 * role. Tenant isolation and per-role/per-org visibility live one layer up, in
 * the dashboard endpoint/config (F2/F3), which reads this view via the admin
 * (`drizzle.admin` / service_role) connection and applies an explicit
 * `WHERE organization_id = $org` filter (same pattern as invoices). SELECT is
 * granted to `service_role` only — never query it as an `authenticated`
 * end-user, which would expose every org's data and bypass the endpoint gate.
 *
 * Soft-deleted interventions/sites/equipment are excluded by the view.
 * `business_model`/`billing_entity_type` default to `'b2b2c'`/`'organization'`
 * only for TRULY site-less rows (`site_id IS NULL`); a row whose site is
 * soft-deleted stays NULL rather than being mislabelled. `metier` is NULL when
 * no equipment is linked.
 */
export const factIntervention = pgView('fact_intervention', {
    id: uuid('id'),
    organizationId: uuid('organization_id'),
    reference: varchar('reference', { length: 20 }),
    status: varchar('status', { length: 50 }).$type(),
    priority: varchar('priority', { length: 50 }).$type(),
    interventionType: varchar('intervention_type', {
        length: 50,
    }).$type(),
    requestedAt: timestamp('requested_at', { withTimezone: true }),
    scheduledAt: timestamp('scheduled_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }),
    updatedAt: timestamp('updated_at', { withTimezone: true }),
    isTemporarySite: boolean('is_temporary_site'),
    visitNumber: integer('visit_number'),
    quoteRequested: boolean('quote_requested'),
    hasDispute: boolean('has_dispute'),
    // Site geo dimensions (NULL for site-less ponctuel rows)
    siteId: uuid('site_id'),
    siteName: text('site_name'),
    region: text('region'),
    city: text('city'),
    postalCode: text('postal_code'),
    department: text('department'),
    latitude: decimal('latitude'),
    longitude: decimal('longitude'),
    businessModel: varchar('business_model', {
        length: 50,
    }).$type(),
    billingEntityType: varchar('billing_entity_type', {
        length: 50,
    }).$type(),
    // Métier (NULL when no equipment linked)
    equipmentId: uuid('equipment_id'),
    metier: varchar('metier', { length: 50 }).$type(),
    equipmentName: text('equipment_name'),
    // Manager (interventions.assigned_by)
    managerId: uuid('manager_id'),
    // Invoice cost rollup (NULL when no SENT/PAID standard invoices)
    invoicedTotalHt: decimal('invoiced_total_ht', { precision: 10, scale: 2 }),
    invoiceCount: integer('invoice_count'),
}).existing();
/**
 * Per-organization counter for intervention references (mirrors
 * invoice_sequences/quote_sequences, but org-scoped: each organization gets
 * its own continuous INT-NNNNN sequence). Incremented atomically via
 * INSERT ... ON CONFLICT DO UPDATE inside the intervention-create transaction.
 */
export const interventionSequences = pgTable('intervention_sequences', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    lastNumber: integer('last_number').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, table => ({
    // One counter row per organization
    organizationUnique: unique('intervention_sequences_unique').on(table.organizationId),
}));
export const interventionNotifications = pgTable('intervention_notifications', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    interventionId: uuid('intervention_id')
        .notNull()
        .references(() => interventions.id, { onDelete: 'cascade' }),
    technicianId: uuid('technician_id')
        .notNull()
        .references(() => technicians.id),
    // Notification type (dispatch, confirmed, cancelled, reminder)
    notificationType: varchar('notification_type', { length: 50 })
        .$type()
        .notNull()
        .default(NotificationType.DISPATCH),
    // Wave info (tier computed by TechnicianMatchingService)
    waveNumber: integer('wave_number').notNull(),
    tier: varchar('tier', { length: 50 }).$type().notNull(),
    // Scheduling
    scheduledAt: timestamp('scheduled_at', { withTimezone: true }).notNull(),
    sentAt: timestamp('sent_at', { withTimezone: true }),
    // Send lease (#1417 Phase 4C) — the durable claim a sender must WIN before
    // it may reach Short.io/Twilio. Both the manual resend path and the 10s
    // dispatch cron claim through the same conditional UPDATE, which is what
    // makes "exactly one send" true; a stale lease is how a crashed sender's
    // row gets picked back up. NULL = never claimed.
    sendLeaseAt: timestamp('send_lease_at', { withTimezone: true }),
    sendAttempt: integer('send_attempt').notNull().default(0),
    // Delivery
    status: varchar('status', { length: 50 })
        .$type()
        .notNull()
        .default(NotificationStatus.PENDING),
    channel: varchar('channel', { length: 50 }).$type(),
    // External IDs
    twilioMessageSid: varchar('twilio_message_sid', { length: 50 }),
    deepLinkUrl: text('deep_link_url'),
    deepLinkToken: text('deep_link_token'),
    // Message content the row cannot re-derive from the intervention (#1686).
    // A CANCELLED message prints an operator-supplied motif that lives only on
    // the event payload — `interventions` has no cancellation_reason column — so
    // it is snapshotted here at queue time. That single snapshot is what the
    // cron re-send and the WhatsApp->SMS fallback both read, so one cancellation
    // never renders two different bodies. NULL on every other notification type.
    cancellationReason: text('cancellation_reason'),
    // #2417: a RESCHEDULED row announces ONE passage. `passage_at` is that
    // passage — what the send re-checks against `interventions.scheduled_at`. `previous_passage_at` is the date the
    // action replaced, struck through in the message; NULL for a NEW passage
    // (« Programmer prochain passage », « À replanifier »), whose previous
    // `scheduled_at` is the visit already done. Both snapshotted at queue time —
    // at send time the intervention only knows the new date. NULL on every other
    // notification type.
    passageAt: timestamp('passage_at', { withTimezone: true }),
    previousPassageAt: timestamp('previous_passage_at', { withTimezone: true }),
    // Error tracking
    errorCode: varchar('error_code', { length: 50 }),
    errorMessage: text('error_message'),
    // Audit
    ...auditTimestamps,
}, table => ({
    pendingIdx: index('idx_notifications_pending')
        .on(table.status, table.scheduledAt)
        .where(sql `${table.status} = 'pending' AND ${table.deletedAt} IS NULL`),
    interventionPendingIdx: index('idx_notifications_intervention_pending')
        .on(table.interventionId, table.status)
        .where(sql `${table.status} = 'pending' AND ${table.deletedAt} IS NULL`),
    interventionIdx: index('idx_notifications_intervention').on(table.interventionId, table.waveNumber, table.createdAt),
    twilioSidIdx: index('idx_notifications_twilio_sid')
        .on(table.twilioMessageSid)
        .where(sql `${table.twilioMessageSid} IS NOT NULL`),
    // CRITICAL (#1417 Phase 4C review finding 1): at most ONE undispatched
    // DISPATCH row per (org, intervention, technician). The send lease serializes
    // contenders for one row ID; it cannot serialize the logical resend
    // OPERATION, because two concurrent resends each cancel (seeing no pending
    // row, since neither can see the other's uncommitted INSERT) and then insert
    // their OWN row — two row IDs, two winning claims, two messages. This index
    // is what makes "one active dispatch per technician" true regardless of
    // caller, so a future third call path cannot reintroduce the race; the loser's
    // INSERT blocks until the winner commits and then fails, which
    // `NotificationsService.resendNotificationToTechnician` turns into a 409.
    // Scoped to PENDING so a sent/failed/cancelled row frees the slot (same
    // "terminal state frees the slot" shape as
    // `invoices_one_commission_per_intervention_idx`), and to DISPATCH so a
    // technician's PENDING reminder can coexist with a PENDING dispatch.
    oneActiveDispatchPerTechnicianIdx: uniqueIndex('notifications_one_active_dispatch_per_technician_idx')
        .on(table.organizationId, table.interventionId, table.technicianId)
        .where(sql `${table.notificationType} = 'dispatch' AND ${table.status} = 'pending' AND ${table.deletedAt} IS NULL`),
    // #1686: the DETERMINISTIC EVENT IDENTITY of a cancellation message. Unlike
    // the dispatch index above this is NOT scoped to a status — a replayed
    // `intervention.status_changed`/`assignment_canceled` event must find the
    // ALREADY SENT row and stop, not a freed slot. (org, intervention,
    // technician) IS the identity: "this technician has been told this
    // intervention is off". The insert is `ON CONFLICT DO NOTHING`, so a replay
    // is a no-op that returns the existing row rather than a 23505.
    // KNOWN TRADEOFF: an operator who unassigns the SAME technician from the
    // SAME intervention twice (unassign -> re-dispatch -> re-accept -> unassign)
    // gets one cancellation message, not two. The second attempt logs
    // "already notified" and loses the claim on the terminal row. Suppressing a
    // duplicate is the behaviour #1686 asks for; widening the key needs an
    // assignment-generation column that does not exist.
    oneCancellationPerTechnicianIdx: uniqueIndex('notifications_one_cancellation_per_technician_idx')
        .on(table.organizationId, table.interventionId, table.technicianId)
        .where(sql `${table.notificationType} = 'cancelled' AND ${table.deletedAt} IS NULL`),
}));
export const interventionReports = pgTable('intervention_reports', {
    id: uuid('id').primaryKey().defaultRandom(),
    interventionId: uuid('intervention_id')
        .notNull()
        .references(() => interventions.id, { onDelete: 'cascade' }),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // Report identification
    reportNumber: integer('report_number').notNull().default(1),
    status: interventionReportStatusEnum('status').default(InterventionReportStatus.DRAFT),
    // Timestamps
    arrivalTime: timestamp('arrival_time', { withTimezone: true }),
    endTime: timestamp('end_time', { withTimezone: true }),
    // Work details
    ticketNumber: varchar('ticket_number', { length: 100 }),
    workDurationMinutes: integer('work_duration_minutes'),
    travelDurationMinutes: integer('travel_duration_minutes'),
    tasks: jsonb('tasks')
        .notNull()
        .default(sql `'[]'::jsonb`),
    // Completion status
    isWorkCompleted: boolean('is_work_completed').notNull().default(false),
    needsSecondVisit: boolean('needs_second_visit').notNull().default(false),
    needsMaterial: boolean('needs_material').notNull().default(false),
    // Faithful completion capture (report-faithful-mirror). Mirrors the 3-way
    // completion state + not-performed reason + follow-up block collected by the
    // technician mobile app. All nullable/additive: today's depanneoApp payload
    // omits them and still validates. `isWorkCompleted`/`needsSecondVisit` above
    // are derived server-side from `completionState` + `followup.choice` when a
    // completion state is provided (backward-compatible otherwise).
    // `completion_state` follows the varchar-for-small-enum precedent of
    // `job_type` (no pgEnum). `followup` jsonb shape (see `ReportFollowup`):
    //   { choice: InterventionFollowupChoice,
    //     returnScheduled?: { date: ISO string, reason: string },
    //     quoteLeftOnsite?: { documentUrl?: string },
    //     quoteToMake?: { description: string, estimatedAmount?: number },
    //     otherFollowup?: { description: string } }
    completionState: varchar('completion_state', {
        length: 32,
    }).$type(),
    notPerformedReason: text('not_performed_reason'),
    followup: jsonb('followup').$type(),
    // Signatures (storage paths)
    clientName: varchar('client_name', { length: 255 }),
    clientSignaturePath: text('client_signature_path'),
    technicianName: varchar('technician_name', { length: 255 }),
    technicianSignaturePath: text('technician_signature_path'),
    // PDF
    pdfPath: text('pdf_path'),
    signedAt: timestamp('signed_at', { withTimezone: true }),
    // Client-visibility gate (report-review-validation). A report authored in the
    // technician app is content-reviewed by a SUPER_ADMIN, then validated here;
    // client roles (ADMIN/FACILITY_MANAGER) only see a report once `validatedAt`
    // is set. SUPER_ADMIN always sees all — the select RLS is org-scoped only
    // (no per-role validation gate), so the client gate lives in
    // `listReportsForWeb`'s app-level filter. Nullable/additive: existing rows
    // are back-filled as validated by migration `0144` so nothing already
    // delivered disappears.
    validatedAt: timestamp('validated_at', { withTimezone: true }),
    validatedBy: uuid('validated_by').references(() => users.id),
    // PDF generation state (#1417 Phase 4). Orthogonal to `status` above — a
    // COMPLETED report's PDF can be GENERATING/READY/FAILED (see
    // ReportPdfGenerationStatus). The web content edit
    // (`InterventionReportsService.updateReportForWeb`) is a two-tx flow: Tx A
    // commits the content change, bumps `contentRevision` and flips GENERATING;
    // the PDF renders/uploads outside any tx; Tx B CAS-attaches `pdf_path` and
    // flips READY (or writes FAILED). Mirrors `quotes.generation_status` (#1417
    // Phase 2). See interventions/AGENTS.md.
    pdfGenerationStatus: varchar('pdf_generation_status', { length: 20 })
        .$type()
        .notNull()
        .default(ReportPdfGenerationStatus.READY),
    // Bumped by every web content edit — the render inputs all live on this row,
    // so any edit invalidates the attached PDF.
    contentRevision: integer('content_revision').notNull().default(1),
    // Which `contentRevision` the currently-attached `pdf_path` renders. NULL
    // means "unknown": either no PDF exists, or it predates revision tracking
    // (migration 0152 defers that backfill out-of-band on purpose — see its
    // header). A non-null value != contentRevision means the PDF is stale.
    documentRevision: integer('document_revision'),
    // Attempts on the CURRENT generation episode: reset to 1 by Tx A, bumped
    // ONLY by InterventionReportPdfReaper's claim (so its attempt cap is exact,
    // unlike quotes.generation_attempt, which ordinary write paths also bump).
    // CAS discriminant so a losing attempt's Tx B can't clobber a newer one.
    pdfGenerationAttempt: integer('pdf_generation_attempt')
        .notNull()
        .default(1),
    // Set when an attempt starts (GENERATING); the reaper reads this to find
    // stale leases.
    pdfGenerationLeaseAt: timestamp('pdf_generation_lease_at', {
        withTimezone: true,
    }),
    // Sanitized {message, code?, at} on FAILED — never a raw stack trace.
    pdfGenerationError: jsonb('pdf_generation_error'),
    // Audit
    ...auditTimestamps,
}, table => ({
    pdfGenerationStatusCheck: check('intervention_reports_pdf_generation_status_check', sql `${table.pdfGenerationStatus} IN ('GENERATING', 'READY', 'FAILED')`),
    interventionIdx: index('intervention_reports_intervention_idx').on(table.interventionId),
    organizationIdx: index('intervention_reports_organization_idx').on(table.organizationId),
    statusIdx: index('intervention_reports_status_idx')
        .on(table.status)
        .where(sql `${table.status} != 'completed'`),
    uniqueReportPerVisit: unique('unique_report_per_visit').on(table.interventionId, table.reportNumber),
}));
export const activityLogs = pgTable('activity_logs', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    entityType: varchar('entity_type', { length: 50 })
        .$type()
        .notNull(),
    entityId: uuid('entity_id').notNull(),
    actionType: varchar('action_type', { length: 100 })
        .$type()
        .notNull(),
    actorId: uuid('actor_id').references(() => users.id),
    actorTechnicianId: uuid('actor_technician_id').references(() => technicians.id),
    metadata: jsonb('metadata'),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    deletedBy: uuid('deleted_by').references(() => users.id),
}, table => ({
    entityIdx: index('activity_logs_entity_idx').on(table.organizationId, table.entityType, table.entityId, table.createdAt),
    // Analytics activity feed (#1265): recent-first per org. Supersedes the
    // former single-column activity_logs_org_idx (org-equality lookups use the
    // leading column of this composite).
    orgCreatedAtIdx: index('activity_logs_org_created_at_idx').on(table.organizationId, table.createdAt.desc()),
    actorIdx: index('activity_logs_actor_idx').on(table.actorId),
    actorTechnicianIdx: index('activity_logs_actor_technician_idx')
        .on(table.actorTechnicianId)
        .where(sql `${table.actorTechnicianId} IS NOT NULL`),
    chkActivityLogsActor: check('chk_activity_logs_actor', sql `(${table.actorId} IS NOT NULL AND ${table.actorTechnicianId} IS NULL) OR (${table.actorId} IS NULL AND ${table.actorTechnicianId} IS NOT NULL)`),
}));
export const userNotifications = pgTable('user_notifications', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    userId: uuid('user_id')
        .notNull()
        .references(() => users.id),
    actorId: uuid('actor_id').references(() => users.id),
    eventType: varchar('event_type', { length: 50 })
        .notNull()
        .$type(),
    entityType: varchar('entity_type', { length: 50 })
        .notNull()
        .$type(),
    // NULLABLE since #1659 (ADR 0002 §9 correction 3): an aggregate notification
    // — the CSV site-import summary of #1481 — is about a batch, not about one
    // row, so there is no id to point at and a placeholder would deep-link
    // somewhere wrong. The `DROP NOT NULL` lands with the engine's schema rather
    // than behind the feature issue because it is expand-safe on its own (the
    // old release keeps writing a value) and because §2 of the contract has to
    // be able to say why `entityId` is NOT part of the occurrence key.
    entityId: uuid('entity_id'),
    title: text('title').notNull(),
    priority: varchar('priority', { length: 20 }).$type(),
    metadata: jsonb('metadata').$type(),
    readAt: timestamp('read_at', { withTimezone: true }),
    /**
     * The engine delivery that wrote this row (#1659), and the reason a retried
     * in-app write cannot mint a SECOND bell row: the adapter inserts
     * `ON CONFLICT DO NOTHING` against the unique index below, so the recovery
     * attempt that exists for a crashed writer is a no-op instead of a
     * duplicate. Deliberately NOT a guessed business key — an id the engine
     * already owns is exact, where `(user, event, entity, ~timestamp)` is a
     * heuristic that would collapse two legitimately distinct notifications.
     *
     * NULL for every row the legacy fan-out wrote, and for every row written
     * before this column existed. That is exactly what `NULLS DISTINCT` (the
     * Postgres default) makes safe here: many NULLs coexist, while two rows can
     * never share one delivery.
     *
     * `ON DELETE SET NULL` because the two tables are purged on different
     * clocks — a bell row can outlive the 90-day delivery that wrote it, and a
     * plain FK would make the engine's retention purge fail on it.
     */
    deliveryId: uuid('delivery_id').references(() => notificationDeliveries.id, { onDelete: 'set null' }),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, table => ({
    userReadCreatedIdx: index('user_notifications_user_read_created_idx').on(table.userId, table.readAt, table.createdAt),
    orgIdx: index('user_notifications_org_idx').on(table.organizationId),
    createdAtIdx: index('user_notifications_created_at_idx').on(table.createdAt),
    /** One bell row per engine delivery — see `deliveryId` for why it is unique. */
    deliveryIdx: uniqueIndex('user_notifications_delivery_idx').on(table.deliveryId),
}));
/** Transport an `organizationNotificationChannels` row posts to (#1551). */
export const notificationChannelKindEnum = pgEnum('NotificationChannelKind', [
    'slack',
    'generic_webhook',
]);
/**
 * Outbound notification channel (#1551): where intervention events get POSTed —
 * a Slack incoming webhook, or the documented generic JSON body.
 *
 * TWO KINDS OF ROW, told apart by whether `organizationId` names an org:
 *  - ORGANISATION channel (`organizationId` set): one client's own feed.
 *  - PLATFORM channel (`organizationId` NULL): Depanneo's own feed, which
 *    receives EVERY organisation's events. NULL means PLATFORM-WIDE here, never
 *    "unknown" — see the column comment and migration 0165 for why the
 *    alternative (one row per org, all pointing at the same URL) silently loses
 *    every newly onboarded client.
 *
 * WHY A TABLE and not jsonb on `organizations`, which is what the comparable
 * per-org list `billingEntities` chose: this one has mutable per-row state.
 * `consecutiveFailures` is bumped by the send path on every failed post, from
 * any of up to `ASSUMED_MAX_REPLICAS` API replicas. In jsonb that is a
 * read-modify-write of the whole `organizations` row, so two concurrent posts
 * lose one increment and the auto-deactivation of a revoked webhook would never
 * fire; one row per channel gives
 * `SET consecutive_failures = consecutive_failures + 1` atomically. Migration
 * 0164 carries the full argument and the additive path to org-managed channels.
 *
 * System table today, not tenant data: the only surface is SUPER_ADMIN-only and
 * reads through `drizzleService.admin` with an explicit `organizationId` filter
 * — the send path adding `OR organization_id IS NULL` for the platform rows — so
 * `authenticated` is never a direct accessor. RLS on with ZERO policies
 * (`rls-classification.ts` NO_RLS_SERVICE_ROLE, same shape as
 * `storage_cleanup_intents`, whose `organizationId` is nullable for the same
 * off-the-tenant-path reason).
 */
export const organizationNotificationChannels = pgTable('organization_notification_channels', {
    id: uuid('id').primaryKey().defaultRandom(),
    // NULL MEANS PLATFORM-WIDE, not "unknown" and not "not filled in yet": the
    // row belongs to Depanneo and receives every organisation's events. The
    // nullability is the whole point — expressing Depanneo's ops feed as one row
    // per org means a newly onboarded client is missing from it until somebody
    // remembers to add a row, with no error to notice (migration 0165). Nullable
    // despite root AGENTS.md rule 3 for the same structural reason
    // `storageCleanupIntents.organizationId` is: this table is not on the
    // RLS-scoped tenant path (NO_RLS_SERVICE_ROLE, see below).
    organizationId: uuid('organization_id').references(() => organizations.id),
    kind: notificationChannelKindEnum('kind').notNull(),
    // The role this channel sees the events AS. Reuses `userRoleEnum` — the
    // enum typing `users.role` — on purpose: the channel is gated by the same
    // role rules the app already applies to a human, so a text column + CHECK
    // would be a second copy of UserRole, free to drift from the first.
    asRole: userRoleEnum('as_role').notNull(),
    targetUrl: text('target_url').notNull(),
    // null = every event in the intervention scope; a list = only those events.
    eventTypes: text('event_types').array(),
    // Human name for ops, e.g. "Ops Depanneo". Display only, never matched on.
    label: text('label'),
    active: boolean('active').notNull().default(true),
    // Bumped atomically by the send path (see the WHY A TABLE note above),
    // reset to 0 on the next success. The auto-deactivation threshold reads it.
    consecutiveFailures: integer('consecutive_failures').notNull().default(0),
    lastSuccessAt: timestamp('last_success_at', { withTimezone: true }),
    lastFailureAt: timestamp('last_failure_at', { withTimezone: true }),
    lastFailureReason: text('last_failure_reason'),
    // Written together with `active = false` when the failure streak trips the
    // threshold, so ops can tell an auto-deactivation (revoked webhook, dead
    // host) from someone switching the channel off by hand.
    deactivatedAt: timestamp('deactivated_at', { withTimezone: true }),
    deactivatedReason: text('deactivated_reason'),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    // FK like every other `deleted_by` in this schema (notes, activity_logs,
    // invoices, quotes): the soft-delete actor must resolve to a real user, or
    // the audit trail records an id nobody can look up.
    deletedBy: uuid('deleted_by').references(() => users.id),
}, table => [
    // Every read is "the channels of THIS org" — the fan-out reads it per event.
    index('organization_notification_channels_org_idx').on(table.organizationId),
    // A cross-org channel is Depanneo's by definition: only SUPER_ADMIN has
    // cross-org visibility in Harmony, so a platform row acting as ADMIN would
    // be handed every organisation's events with a single client's visibility —
    // one client's Slack learning that another client exists. Postgres refuses
    // that rather than trusting the code to, because this table has no RLS to
    // catch the mistake and the `as_role` gate runs long after the row is
    // written.
    check('organization_notification_channels_platform_is_super_admin', sql `${table.organizationId} IS NOT NULL OR ${table.asRole} = 'SUPER_ADMIN'`),
]);
/* ==========================================================================
 * UNIFIED NOTIFICATION ENGINE (#1659)
 * Contract: docs/decisions/0002-unified-notification-engine.md. ONE TABLE: a
 * delivery is one recipient × one channel, and it carries the fact it is about.
 *
 * WHY NOT A SEPARATE OCCURRENCE TABLE (Simon, 2026-09-02, reworking §1.1/§2 of
 * the ADR): none of the contract's invariants needed one. Dedupe holds on a
 * single unique key over this row; replay re-attempts THIS row and never
 * re-decides an audience; retention is this table against `user_notifications`,
 * not occurrence against delivery; and crash recovery only ever reads
 * deliveries, so a parent row with no deliveries was never picked up by
 * anything. What such a table would have recorded — the fact, its actor and its
 * metadata, even with zero recipients — is what `activity_logs` already stores,
 * so it was a conceptual duplicate of that, not of this. The cost is `facts`
 * (a few hundred bytes of ids) duplicated across the 1–10 recipients of one
 * fact; accepted deliberately.
 *
 * OUT OF SCOPE, permanently: technician WhatsApp/SMS dispatch
 * (`interventionNotifications` — its retry policy is a business rule, its rows
 * authorize the technician app, and its lifecycle is waves/tiers plus a
 * Twilio-webhook SMS fallback) and the authentication-mail flow, which brokers
 * credential-bearing links the engine never carries.
 * ========================================================================== */
/** Channel a delivery is sent on (#1659). Mirrors `NotificationDeliveryChannel`. */
export const notificationDeliveryChannelEnum = pgEnum('NotificationDeliveryChannel', enumToPgEnum(NotificationDeliveryChannel));
/** Channel-independent delivery state (#1659). Mirrors `NotificationDeliveryState`. */
export const notificationDeliveryStateEnum = pgEnum('NotificationDeliveryState', enumToPgEnum(NotificationDeliveryState));
/**
 * ONE RECIPIENT, ONE CHANNEL, ONE FACT (#1659, ADR 0002 §1.3).
 *
 * The unit of retry, of state and of the monitoring surface — and, since the
 * occurrence table was dropped, the carrier of the fact itself. The fact columns
 * (`type`, `dedupeToken`, `facts`, the entity and the actor) are IMMUTABLE after
 * insert: they describe what happened, and re-deciding them later would deliver
 * a message that no longer matches the event it announces. Only the send columns
 * below them ever change.
 *
 * Channel failures never interact: an email failure never rolls back an invoice
 * and never suppresses the in-app row.
 *
 * AN IN-APP DELIVERY ROW IS NOT THE BELL ROW. `user_notifications` stays the
 * user-facing artifact the bell reads and marks read; this row is the engine's
 * record that it was written, and `user_notifications.delivery_id` points back
 * here so a retried write can never mint a second bell row. They keep separate
 * retention (90 days here; 30 for a read bell row), which is why they are
 * separate rows at all.
 *
 * No role logic and no say in who it is for: the audience layer decided that
 * before this row existed.
 *
 * RLS: NO_RLS_SERVICE_ROLE (`src/drizzle/rls-classification.ts`) — RLS enabled
 * with ZERO policies, same shape as `organization_notification_channels`. The
 * writers are listeners and a sweep with no request scope, and the only read
 * surface planned (#1664 monitoring/replay) is SUPER_ADMIN-only through the
 * admin client with an explicit `organization_id` filter, so `authenticated` is
 * never a direct accessor. The day a tenant reads its own deliveries this
 * becomes TENANT_SCOPED with a policy per DML command.
 */
export const notificationDeliveries = pgTable('notification_deliveries', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // ---- THE FACT (immutable after insert) --------------------------------
    /**
     * The domain event name (`AppEventName`), never the in-app bell's
     * `InAppNotificationEventType`. Those answer different questions, and one
     * enum doing both is why `intervention.status_forced` has no member of its
     * own today and borrows another type's identity to pass a gate. Here the
     * type is the FACT; the bell's presentation type is a per-delivery render
     * concern.
     */
    type: varchar('type', { length: 100 }).notNull().$type(),
    /**
     * The emitting domain's identity for this fact, and half of the idempotency
     * key below.
     *
     * It must derive from the STATE TRANSITION, never from a clock: a status
     * change uses the transition it wrote, a signature uses the report id. That
     * is what makes the same domain event, emitted twice by two replicas or by a
     * retried webhook, one delivery per recipient — and what lets the three
     * events a single technician signature emits collapse into the one fact they
     * always were, by sharing a token.
     */
    dedupeToken: text('dedupe_token').notNull(),
    /**
     * Identifiers and state, never content (ADR 0002 §5). Typed as scalars only
     * so "just attach the object we already have" does not compile.
     */
    facts: jsonb('facts')
        .$type()
        .notNull()
        .default({}),
    entityType: varchar('entity_type', { length: 50 }).$type(),
    /**
     * Nullable, like `user_notifications.entity_id` since #1659: an aggregate
     * fact (#1481's CSV site import) is about a batch and has no single entity.
     * It is deliberately NOT part of the idempotency key — a nullable column
     * there would silently disable the constraint, since Postgres unique indexes
     * are `NULLS DISTINCT`.
     */
    entityId: uuid('entity_id'),
    /** The user who acted, when a user did. A technician-actored fact uses the column below. */
    actorId: uuid('actor_id').references(() => users.id),
    actorTechnicianId: uuid('actor_technician_id').references(() => technicians.id),
    occurredAt: timestamp('occurred_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    // ---- WHO AND WHERE ----------------------------------------------------
    channel: notificationDeliveryChannelEnum('channel').notNull(),
    /**
     * WHO, as an id — never an address. For `in_app` and for a `CLIENT_EMAIL`
     * delivery it is `users.id`; for `slack`/`generic_webhook` it is
     * `organization_notification_channels.id`. Polymorphic, so no FK; `uuid`
     * rather than `text` so a recipient's email address cannot end up stored
     * here (§5 forbids it) — the address is resolved at SEND time, from the
     * live user row, which is also what honours an address changed since the
     * fact.
     *
     * ONE SENDER POINTS AT SOMETHING ELSE, DELIBERATELY: an `INVOICE_EMAIL`
     * delivery holds the INVOICE id (#1662). Its destination is
     * `clientSnapshot.email` — or `technicianSnapshot.email` for a commission —
     * on the immutable issued snapshot, and an external billing address is a
     * contractual destination with frequently no `users` row to name. The billed
     * party is therefore identified by the invoice whose snapshot names them,
     * which is also what gives the idempotency key below its meaning there: one
     * initial email per issuance, however often the event is reprocessed.
     */
    recipientRef: uuid('recipient_ref').notNull(),
    /**
     * The channel's sub-target when it has one (a second Slack channel in one
     * org), and the EMPTY STRING when it does not — never NULL. See the unique
     * index below: a nullable column here would silently disable it for every
     * in-app and email delivery, which are exactly the ones with no sub-target.
     */
    channelTargetRef: text('channel_target_ref').notNull().default(''),
    /** The legacy sender this delivery is migrating off — its feature flag's subject. */
    sender: varchar('sender', { length: 32 })
        .notNull()
        .$type(),
    // ---- THE SEND (the only mutable half) ---------------------------------
    state: notificationDeliveryStateEnum('state')
        .notNull()
        .default(NotificationDeliveryState.PENDING),
    sendAttempt: integer('send_attempt').notNull().default(0),
    /**
     * Attempts this channel's retry policy allows (ADR 0002 §7: 5 for email, 3
     * for Slack/webhook, 2 for in-app — the second being the crashed-writer
     * recovery, not a retry; `RETRY_BACKOFF_MS` is the source of truth and says
     * why). Stored per row rather than derived, so the claim's
     * `send_attempt < max_attempts` guard is one predicate instead of a CASE
     * over channels — and so a policy change never retroactively revives a
     * delivery that was already given up on.
     */
    maxAttempts: integer('max_attempts').notNull(),
    /**
     * The durable claim a sender must WIN before it may reach a vendor, stamped
     * with the DB's `now()`. NULL = never claimed. A crashed sender's row is
     * recovered by the next sweep once this goes stale — there is no reaper.
     */
    sendLeaseAt: timestamp('send_lease_at', { withTimezone: true }),
    /**
     * When the next attempt becomes due. The backoff HAS to be a column:
     * nothing else schedules attempt 2, and the sweep's third arm (`FAILED`
     * with attempts left and `next_attempt_at <= now()`) is what makes retries
     * fire at all.
     */
    nextAttemptAt: timestamp('next_attempt_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    sentAt: timestamp('sent_at', { withTimezone: true }),
    /** Vendor id kept for support: Brevo's messageId, and nothing sensitive. */
    providerMessageId: varchar('provider_message_id', { length: 255 }),
    /**
     * Channel-independent, SANITIZED failure metadata. A webhook URL is a bearer
     * credential and an exception message is only PII-redacted, not dropped — so
     * what lands here is a short classification with every URL stripped, never
     * the vendor's raw error text.
     */
    failureReason: text('failure_reason'),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, table => [
    /**
     * THE IDEMPOTENCY KEY, and there is only one now: the fact and the
     * destination together.
     *
     * EVERY COLUMN IN IT IS NOT NULL, and that is the whole design rather than a
     * detail: Postgres unique indexes are `NULLS DISTINCT` by default, so one
     * nullable column would make the constraint silently stop deduplicating —
     * and it would stop precisely where it matters (`channel_target_ref` is
     * `''`, not NULL, for the in-app and email deliveries that have no
     * sub-target; `entity_id` is nullable and therefore excluded). We do not
     * rely on `NULLS NOT DISTINCT`.
     *
     * It does two jobs at once, which is why the occurrence table was
     * redundant: the same fact published twice inserts nothing the second time,
     * and re-running the audience resolution CONVERGES on the same rows instead
     * of duplicating them. A send path that deduped in memory would dedupe once
     * per replica, and the API runs up to `ASSUMED_MAX_REPLICAS` (4).
     */
    uniqueIndex('notification_deliveries_dedupe_idx').on(table.organizationId, table.type, table.dedupeToken, table.recipientRef, table.channel, table.channelTargetRef),
    // The sweep's candidate SELECT: due work, oldest first. Partial so the index
    // holds only rows that can still be claimed — a SENT/EXHAUSTED/CANCELLED row
    // never returns to it, so the index stays small while the table keeps 90
    // days of history.
    index('notification_deliveries_due_idx')
        .on(table.nextAttemptAt)
        .where(sql `${table.state} IN ('pending', 'sending', 'failed')`),
    // #1664 reads one org's recent deliveries; the 90-day purge deletes on
    // `created_at` alone (one DELETE, no cascade to follow).
    index('notification_deliveries_org_created_idx').on(table.organizationId, table.createdAt),
    index('notification_deliveries_created_at_idx').on(table.createdAt),
]);
/**
 * Global, org-less standard equipment catalog. A single shared reference list of
 * equipment templates (seeded from the bundled CSV) that a SUPER_ADMIN curates.
 * On org creation / catalog import these rows are copied into `equipment` as
 * per-org catalog items (isCatalogItem=true). No organization_id and no
 * created_by/updated_by/deleted_by — the catalog is not owned by any tenant.
 * job_type stays a plain varchar (no CHECK), consistent with `equipment`.
 */
export const equipmentCatalog = pgTable('equipment_catalog', {
    id: uuid('id').primaryKey().defaultRandom(),
    name: text('name').notNull(),
    brand: text('brand'),
    model: text('model'),
    category: text('category'),
    subCategory: text('sub_category'),
    jobType: varchar('job_type', { length: 50 }).$type().notNull(),
    isActive: boolean('is_active').default(true).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true })
        .defaultNow()
        .notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .defaultNow()
        .notNull(),
});
export const technicians = pgTable('technicians', {
    id: uuid('id').primaryKey().defaultRandom(),
    v1LegacyId: integer('v1_legacy_id').unique(),
    companyName: text('company_name').notNull(),
    website: text('website'),
    siren: varchar('siren', { length: 9 }).unique(),
    siret: varchar('siret', { length: 14 }).unique(),
    vatNumber: varchar('vat_number', { length: 20 }),
    legalName: text('legal_name'),
    companyType: varchar('company_type', { length: 50 }).$type(),
    nafCode: varchar('naf_code', { length: 10 }),
    address: text('address').notNull(),
    addressLine2: text('address_line2'),
    city: text('city').notNull(),
    postalCode: varchar('postal_code', { length: 10 }).notNull(),
    department: varchar('department', { length: 3 }),
    country: varchar('country', { length: 2 }).default('FR'),
    gmapsPlaceId: text('gmaps_place_id'),
    latitude: decimal('latitude', { precision: 10, scale: 8 }),
    longitude: decimal('longitude', { precision: 11, scale: 8 }),
    billingAddress: jsonb('billing_address')
        .$type()
        .default(sql `'{"useOperationalAddress": true}'::jsonb`),
    billingContactId: uuid('billing_contact_id'),
    acceptedPaymentMethods: text('accepted_payment_methods')
        .array()
        .$type()
        .default(sql `ARRAY[]::text[]`),
    paymentMethodsConfig: jsonb('payment_methods_config'),
    rib: text('rib'),
    gocardlessId: text('gocardless_id'),
    archive: boolean('archive').default(false).notNull(),
    workersCount: integer('workers_count').default(1).notNull(),
    observesJewishCalendar: boolean('observes_jewish_calendar')
        .default(false)
        .notNull(),
    acceptsWeekends: boolean('accepts_weekends').default(true).notNull(),
    acceptsHolidays: boolean('accepts_holidays').default(true).notNull(),
    performanceNote: decimal('performance_note', { precision: 5, scale: 2 }),
    performanceRank: integer('performance_rank'),
    performanceTotalRated: integer('performance_total_rated'),
    totalInterventionsCompleted: integer('total_interventions_completed')
        .default(0)
        .notNull(),
    missingRequiredDocsCount: integer('missing_required_docs_count')
        .default(6)
        .notNull(),
    docsLastCheckedAt: timestamp('docs_last_checked_at', {
        withTimezone: true,
    }),
    contractStatus: text('contract_status')
        .notNull()
        .$type(),
    isBlocked: boolean('is_blocked').default(false).notNull(),
    status: technicianStatusEnum('status')
        .default(TechnicianStatus.PENDING_ACTIVATION)
        .notNull(),
    syncStatus: varchar('sync_status', { length: 20 })
        .$type()
        .default(SyncStatus.PENDING),
    lastSyncedAt: timestamp('last_synced_at', { withTimezone: true }),
    // Audit Fields
    ...auditTimestamps,
}, table => ({
    // Status index with partial WHERE clause (only active records)
    statusIdx: index('idx_technicians_status')
        .on(table.status)
        .where(sql `${table.deletedAt} IS NULL AND ${table.archive} = false`),
    // V1 legacy ID index (only non-null values)
    v1LegacyIdx: uniqueIndex('idx_technicians_v1_legacy')
        .on(table.v1LegacyId)
        .where(sql `${table.v1LegacyId} IS NOT NULL`),
    // SIREN index (only non-null values)
    sirenIdx: uniqueIndex('idx_technicians_siren')
        .on(table.siren)
        .where(sql `${table.siren} IS NOT NULL`),
    // SIRET index (only non-null values)
    siretIdx: uniqueIndex('idx_technicians_siret')
        .on(table.siret)
        .where(sql `${table.siret} IS NOT NULL`),
    // Performance index for ranking queries
    performanceIdx: index('idx_technicians_performance')
        .on(table.performanceRank, table.performanceNote)
        .where(sql `${table.deletedAt} IS NULL AND ${table.archive} = false`),
    // CRITICAL: Scheduling fast index for matching algorithm
    // Includes accepts_holidays for complete weekend/holiday filtering
    schedulingFastIdx: index('idx_technicians_scheduling_fast')
        .on(table.status, table.observesJewishCalendar, table.acceptsWeekends, table.acceptsHolidays)
        .where(sql `${table.deletedAt} IS NULL AND ${table.archive} = false`),
    // GIN index for payment methods array (contains queries)
    paymentMethodsIdx: index('idx_technicians_payment_methods')
        .using('gin', table.acceptedPaymentMethods)
        .where(sql `${table.deletedAt} IS NULL`),
    // Docs status index for compliance dashboard
    docsStatusIdx: index('idx_technicians_docs_status')
        .on(table.status, table.missingRequiredDocsCount)
        .where(sql `${table.deletedAt} IS NULL`),
    // Check Constraints
    workersCountCheck: check('check_workers_count', sql `${table.workersCount} >= 1`),
    latitudeRangeCheck: check('check_latitude_range', sql `${table.latitude} >= -90 AND ${table.latitude} <= 90`),
    longitudeRangeCheck: check('check_longitude_range', sql `${table.longitude} >= -180 AND ${table.longitude} <= 180`),
}));
export const technicianWorkZones = pgTable('technician_work_zones', {
    id: uuid('id').primaryKey().defaultRandom(),
    technicianId: uuid('technician_id')
        .notNull()
        .references(() => technicians.id, { onDelete: 'cascade' }),
    zoneType: zoneTypeEnum('zone_type').notNull(),
    postalCode: varchar('postal_code', { length: 10 }),
    radiusKm: integer('radius_km'),
    latitude: decimal('latitude', { precision: 10, scale: 8 }),
    longitude: decimal('longitude', { precision: 11, scale: 8 }),
    departmentCode: varchar('department_code', { length: 3 }),
    commissionPercentage: decimal('commission_percentage', {
        precision: 5,
        scale: 2,
    })
        .default('15.00')
        .notNull(),
    jobType: varchar('job_type', { length: 50 }).$type().notNull(),
    exclusive: boolean('exclusive').default(false).notNull(),
    isActive: boolean('is_active').default(true).notNull(),
    ...auditTimestamps,
}, table => ({
    technicianIdx: index('idx_work_zones_technician')
        .on(table.technicianId, table.zoneType, table.isActive)
        .where(sql `${table.deletedAt} IS NULL`),
    postalIdx: index('idx_work_zones_postal')
        .on(table.postalCode, table.radiusKm)
        .where(sql `${table.zoneType} = ${ZoneType.POSTAL_CODE} AND ${table.deletedAt} IS NULL`),
    departmentIdx: index('idx_work_zones_department')
        .on(table.departmentCode)
        .where(sql `${table.zoneType} = ${ZoneType.DEPARTMENT} AND ${table.deletedAt} IS NULL`),
    jobTypeIdx: index('idx_work_zones_job_type')
        .on(table.jobType)
        .where(sql `${table.deletedAt} IS NULL`),
    // Composite index for matching queries (job_type + zone_type + is_active)
    matchingIdx: index('idx_work_zones_matching')
        .on(table.jobType, table.zoneType, table.isActive)
        .where(sql `${table.deletedAt} IS NULL`),
    // PERFORMANCE INDEXES (defined in migrations 0076 & 0077):
    // - idx_work_zones_spatial_gist: GiST on geography expression for ST_DWithin (postal_code zones)
    // - idx_work_zones_department: Composite (department_code, job_type) for department zone lookups
    // See: 0076_matching_query_optimization.sql, 0077_fix_matching_indexes.sql
    zoneTypeFieldsCheck: check('check_zone_type_fields', sql `
        (${table.zoneType} = ${ZoneType.POSTAL_CODE} AND
         ${table.postalCode} IS NOT NULL AND
         ${table.radiusKm} IS NOT NULL AND
         ${table.latitude} IS NOT NULL AND
         ${table.longitude} IS NOT NULL)
        OR
        (${table.zoneType} = ${ZoneType.DEPARTMENT} AND ${table.departmentCode} IS NOT NULL)
      `),
    radiusPositiveCheck: check('check_radius_positive', sql `${table.radiusKm} IS NULL OR ${table.radiusKm} > 0`),
    commissionMinCheck: check('check_commission_min', sql `${table.commissionPercentage} >= 15.0`),
}));
export const technicianAvailability = pgTable('technician_availability', {
    id: uuid('id').primaryKey().defaultRandom(),
    technicianId: uuid('technician_id')
        .notNull()
        .references(() => technicians.id, { onDelete: 'cascade' }),
    availabilityType: availabilityTypeEnum('availability_type').notNull(),
    dayOfWeek: integer('day_of_week'), // 0=Sunday, 1=Monday, ..., 6=Saturday
    timeStart: time('time_start'), // HH:MM:SS format
    timeEnd: time('time_end'),
    is247: boolean('is_24_7').default(false).notNull(),
    unavailableFromAt: timestamp('unavailable_from_at', { withTimezone: true }),
    unavailableToAt: timestamp('unavailable_to_at', { withTimezone: true }),
    unavailabilityReason: varchar('unavailability_reason', {
        length: 50,
    }).$type(),
    isActive: boolean('is_active').default(true).notNull(),
    ...auditTimestamps,
}, table => ({
    technicianTypeIdx: index('idx_availability_technician_type')
        .on(table.technicianId, table.availabilityType, table.isActive)
        .where(sql `${table.deletedAt} IS NULL`),
    weeklyIdx: index('idx_availability_weekly')
        .on(table.technicianId, table.dayOfWeek, table.timeStart, table.timeEnd)
        .where(sql `${table.availabilityType} = ${AvailabilityType.WEEKLY_SCHEDULE} AND ${table.isActive} = true AND ${table.deletedAt} IS NULL`),
    // PERFORMANCE INDEXES (defined in migration 0076_matching_query_optimization.sql):
    // - idx_availability_weekly_optimized: Composite covering index for opening hours EXISTS subqueries
    // - idx_availability_periods_gist: GiST on tstzrange(unavailable_from_at, unavailable_to_at) for vacation @> queries
    // Constraints
    weeklyFieldsCheck: check('check_weekly_fields', sql `
        (${table.availabilityType} = ${AvailabilityType.WEEKLY_SCHEDULE} AND
         ${table.dayOfWeek} IS NOT NULL AND
         (${table.timeStart} IS NOT NULL OR ${table.is247} = true) AND
         (${table.timeEnd} IS NOT NULL OR ${table.is247} = true))
        OR
        ${table.availabilityType} != ${AvailabilityType.WEEKLY_SCHEDULE}
      `),
    unavailabilityTwinsCheck: check('check_unavailability_twins', sql `
        (${table.availabilityType} = ${AvailabilityType.UNAVAILABILITY_PERIOD} AND
         ${table.unavailableFromAt} IS NOT NULL AND
         ${table.unavailableToAt} IS NOT NULL AND
         ${table.unavailableToAt} > ${table.unavailableFromAt})
        OR
        ${table.availabilityType} != ${AvailabilityType.UNAVAILABILITY_PERIOD}
      `),
    timeRangeCheck: check('check_time_range', sql `
        ${table.timeEnd} > ${table.timeStart} OR
        ${table.is247} = true OR
        ${table.timeEnd} IS NULL
      `),
    dayOfWeekCheck: check('check_day_of_week', sql `
        ${table.dayOfWeek} IS NULL OR
        (${table.dayOfWeek} >= 0 AND ${table.dayOfWeek} <= 6)
      `),
}));
export const technicianContacts = pgTable('technician_contacts', {
    id: uuid('id').primaryKey().defaultRandom(),
    technicianId: uuid('technician_id')
        .notNull()
        .references(() => technicians.id, { onDelete: 'cascade' }),
    firstName: text('first_name').notNull(),
    lastName: text('last_name').notNull(),
    email: text('email'),
    phone: varchar('phone', { length: 20 }),
    phoneCountryCode: varchar('phone_country_code', { length: 5 }).default('+33'),
    contactRoles: text('contact_roles')
        .array()
        .notNull()
        .$type(),
    isPrimary: boolean('is_primary').default(false).notNull(),
    isActive: boolean('is_active').default(true).notNull(),
    ...auditTimestamps,
}, table => ({
    // Indexes
    technicianIdx: index('idx_contacts_technician')
        .on(table.technicianId)
        .where(sql `${table.deletedAt} IS NULL`),
    contactRolesIdx: index('idx_contacts_roles')
        .using('gin', table.contactRoles)
        .where(sql `${table.deletedAt} IS NULL`),
    technicianContactComposite: unique('uk_technician_contact').on(table.technicianId, table.id),
    primaryIdx: uniqueIndex('idx_contacts_primary')
        .on(table.technicianId)
        .where(sql `${table.isPrimary} = true`),
    emailIdx: index('idx_contacts_email')
        .on(table.email)
        .where(sql `${table.email} IS NOT NULL AND ${table.deletedAt} IS NULL`),
    // Constraints
    phoneFormatCheck: check('check_phone_format', sql `${table.phone} ~ '^[0-9\\s\\-\\+\\(\\)]+$'`),
}));
export const technicianFlags = pgTable('technician_flags', {
    id: uuid('id').primaryKey().defaultRandom(),
    technicianId: uuid('technician_id')
        .notNull()
        .references(() => technicians.id, { onDelete: 'cascade' }),
    flagType: varchar('flag_type', { length: 50 })
        .notNull()
        .$type(),
    isActive: boolean('is_active').default(true).notNull(),
    validFromAt: timestamp('valid_from_at', { withTimezone: true }),
    validUntilAt: timestamp('valid_until_at', { withTimezone: true }),
    reason: text('reason').notNull(),
    setBy: uuid('set_by')
        .notNull()
        .references(() => users.id),
    ...auditTimestamps,
}, table => ({
    // Index for active flags lookup
    // Note: Cannot include an expiry test in the index predicate (NOW() is not immutable)
    // Application queries add: WHERE (valid_until_at IS NULL OR valid_until_at > NOW())
    technicianTypeIdx: index('idx_flags_technician_type')
        .on(table.technicianId, table.flagType, table.isActive)
        .where(sql `${table.isActive} = true`),
    typeIdx: index('idx_flags_type')
        .on(table.flagType)
        .where(sql `${table.isActive} = true`),
    expiringIdx: index('idx_flags_expiring')
        .on(table.validUntilAt)
        .where(sql `${table.validUntilAt} IS NOT NULL AND ${table.isActive} = true`),
    // Constraints
    validityPeriodTwinsCheck: check('check_validity_period_twins', sql `
        ${table.validUntilAt} IS NULL OR ${table.validUntilAt} > ${table.validFromAt}
      `),
    removedLogicCheck: check('check_removed_logic', sql `
        (${table.deletedBy} IS NULL AND ${table.deletedAt} IS NULL) OR
        (${table.deletedBy} IS NOT NULL AND ${table.deletedAt} IS NOT NULL)
      `),
}));
export const technicianInterventionActions = pgTable('technician_intervention_actions', {
    id: uuid('id').primaryKey().defaultRandom(),
    technicianId: uuid('technician_id')
        .notNull()
        .references(() => technicians.id, { onDelete: 'cascade' }),
    interventionId: uuid('intervention_id')
        .notNull()
        .references(() => interventions.id, { onDelete: 'cascade' }),
    actionType: varchar('action_type', { length: 50 })
        .notNull()
        .$type(),
    reason: text('reason'),
    createdAt: timestamp('created_at', { withTimezone: true }).defaultNow(),
}, table => ({
    uniqueTechInterventionAction: unique('uk_tech_intervention_action').on(table.technicianId, table.interventionId, table.actionType),
    matchingFilterIdx: index('idx_tech_actions_matching_filter')
        .on(table.technicianId, table.interventionId, table.actionType, table.createdAt)
        .where(sql `${table.actionType} IN (${TechnicianInterventionActionType.REJECTED}, ${TechnicianInterventionActionType.CANCELED})`),
    analyticsIdx: index('idx_tech_actions_analytics')
        .on(table.technicianId, table.actionType, table.createdAt)
        .where(sql `${table.actionType} IN (${TechnicianInterventionActionType.REJECTED}, ${TechnicianInterventionActionType.ACCEPTED}, ${TechnicianInterventionActionType.CANCELED})`),
    // Intervention history index (all actions for an intervention, ordered by date DESC)
    interventionHistoryIdx: index('idx_tech_actions_by_intervention').on(table.interventionId, table.createdAt.desc()),
    // Technician lookup index (all actions by a technician)
    technicianIdx: index('idx_tech_actions_by_technician').on(table.technicianId, table.createdAt.desc()),
}));
export const platformBillingEntity = pgTable('platform_billing_entity', {
    id: uuid('id').primaryKey().defaultRandom(),
    // Legal Identity (required for French invoices)
    legalName: varchar('legal_name', { length: 255 }).notNull(),
    businessName: varchar('business_name', { length: 255 }),
    legalForm: varchar('legal_form', { length: 100 }),
    siret: varchar('siret', { length: 14 }).notNull(),
    vatNumber: varchar('vat_number', { length: 20 }),
    rcsNumber: varchar('rcs_number', { length: 100 }),
    shareCapital: varchar('share_capital', { length: 50 }),
    // Address
    addressLine1: varchar('address_line1', { length: 255 }).notNull(),
    addressLine2: varchar('address_line2', { length: 255 }),
    postalCode: varchar('postal_code', { length: 10 }).notNull(),
    city: varchar('city', { length: 100 }).notNull(),
    country: varchar('country', { length: 100 }).default('France'),
    // Contact
    email: varchar('email', { length: 255 }),
    phone: varchar('phone', { length: 50 }),
    website: varchar('website', { length: 255 }),
    // Bank Details (for payment)
    beneficiary: varchar('beneficiary', { length: 255 }),
    bankName: varchar('bank_name', { length: 255 }),
    iban: varchar('iban', { length: 34 }),
    bic: varchar('bic', { length: 11 }),
    // Default Legal Texts
    invoiceConditionsText: text('invoice_conditions_text'),
    quoteConditionsText: text('quote_conditions_text'),
    defaultInvoiceFooter: text('default_invoice_footer').notNull(),
    defaultQuoteFooter: text('default_quote_footer').notNull(),
    vatExemptionText: text('vat_exemption_text'),
    // Config
    paymentDelayDays: integer('payment_delay_days').default(30),
    quoteValidityDays: integer('quote_validity_days').default(30),
    // Audit
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedBy: uuid('updated_by').references(() => users.id),
}, () => ({
    // Single row constraint (only one billing entity record)
    singleton: uniqueIndex('platform_billing_entity_singleton').on(sql `(true)`),
}));
export const invoiceSequences = pgTable('invoice_sequences', {
    id: uuid('id').primaryKey().defaultRandom(),
    /** Legal issuer: `T` + technician `v1_legacy_id` padded to 4, or `DPNO`. */
    entityCode: varchar('entity_code', { length: 10 }).notNull(),
    year: integer('year').notNull(),
    prefix: varchar('prefix', { length: 10 }).notNull(),
    lastNumber: integer('last_number').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, table => ({
    // One series per legal issuer per year per PRINTED prefix (#1505 Phase 5,
    // migration 0163). Keyed on `prefix` and NOT on the internal `invoice_type`
    // on purpose: STANDARD and COMMISSION both print `FAC`, so a type-keyed row
    // would give Depanneo two counters emitting the same number (#1462).
    entityYearPrefixUnique: unique('invoice_sequences_unique').on(table.entityCode, table.year, table.prefix),
}));
export const quoteSequences = pgTable('quote_sequences', {
    id: uuid('id').primaryKey().defaultRandom(),
    year: integer('year').notNull(),
    prefix: varchar('prefix', { length: 10 }).notNull().default('DEV'),
    lastNumber: integer('last_number').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, table => ({
    // Global sequence: one row per year
    yearUnique: unique('quote_sequences_unique').on(table.year),
}));
export const invoices = pgTable('invoices', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // Numbering
    invoiceNumber: varchar('invoice_number', { length: 50 }),
    invoiceType: varchar('invoice_type', { length: 20 })
        .$type()
        .notNull()
        .default(InvoiceType.STANDARD),
    status: varchar('status', { length: 20 })
        .$type()
        .notNull()
        .default(InvoiceStatus.DRAFT),
    // Relationships
    interventionId: uuid('intervention_id').references(() => interventions.id),
    quoteId: uuid('quote_id'), // Forward reference, constraint added after quotes table
    technicianId: uuid('technician_id').references(() => technicians.id),
    parentInvoiceId: uuid('parent_invoice_id'), // Self-reference for credit notes
    documentId: uuid('document_id').references(() => documents.id),
    // Snapshots (frozen at creation - preserves data even if source changes)
    issuerSnapshot: jsonb('issuer_snapshot').notNull(),
    technicianSnapshot: jsonb('technician_snapshot').notNull(),
    clientSnapshot: jsonb('client_snapshot').notNull(),
    bankDetails: jsonb('bank_details').notNull(),
    // Financials
    subtotalHt: decimal('subtotal_ht', { precision: 10, scale: 2 })
        .notNull()
        .default('0'),
    globalDiscountType: varchar('global_discount_type', {
        length: 20,
    }).$type(),
    globalDiscountValue: decimal('global_discount_value', {
        precision: 10,
        scale: 2,
    }),
    globalDiscountHt: decimal('global_discount_ht', {
        precision: 10,
        scale: 2,
    }).default('0'),
    // The printed name of the global discount line, frozen at creation like the
    // snapshots: the org's `mandatoryDiscountLabel` when its mandatory discount
    // was applied (#2672), copied by an avoir from its parent. Null prints the
    // default « Remise globale ».
    globalDiscountLabel: text('global_discount_label'),
    totalHt: decimal('total_ht', { precision: 10, scale: 2 })
        .notNull()
        .default('0'),
    totalVat: decimal('total_vat', { precision: 10, scale: 2 })
        .notNull()
        .default('0'),
    totalTtc: decimal('total_ttc', { precision: 10, scale: 2 })
        .notNull()
        .default('0'),
    vatBreakdown: jsonb('vat_breakdown').notNull().default([]),
    // Dates
    issueDate: date('issue_date').notNull().defaultNow(),
    serviceDate: date('service_date').notNull(),
    dueDate: date('due_date').notNull(),
    sentAt: timestamp('sent_at', { withTimezone: true }),
    paidAt: timestamp('paid_at', { withTimezone: true }),
    cancelledAt: timestamp('cancelled_at', { withTimezone: true }),
    // Client-initiated payment declaration (#1176). Optional nudge — does NOT
    // change status; SUPER_ADMIN mark-paid (paidAt) remains the source of truth.
    clientDeclaredPaymentAt: timestamp('client_declared_payment_at', {
        withTimezone: true,
    }),
    // Content
    legalFooter: text('legal_footer').notNull(),
    note: text('note'),
    // Audit
    // createdBy is nullable for technician-created drafts (technicians are not users)
    // Required at application level when invoice is SENT (French law requirement)
    // See migration 0073_invoice_created_by_nullable.sql for full documentation
    createdAt: timestamp('created_at', { withTimezone: true }).defaultNow(),
    createdBy: uuid('created_by').references(() => users.id),
    updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow(),
    updatedBy: uuid('updated_by').references(() => users.id),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    deletedBy: uuid('deleted_by').references(() => users.id),
}, table => ({
    // Unique invoice number per organization
    numberUnique: unique('invoices_number_unique').on(table.organizationId, table.invoiceNumber),
    // GLOBAL uniqueness on the printed number (#1505 Phase 4, migration 0162).
    // Numbers are platform-global (`invoice_sequences` has no organization_id) and
    // reconciliation resolves a pasted number across every tenant, so the per-org
    // constraint above cannot catch the cross-org duplicate #1462 makes possible.
    // DELIBERATELY NOT partial on `deleted_at IS NULL`: a soft-deleted issued number
    // is still burnt and must never be reused. Drafts carry NULL and, since Postgres
    // indexes treat NULLs as distinct, never conflict with each other.
    // `invoices_number_unique` and `invoices_invoice_number_idx` are now redundant but
    // are deliberately KEPT — dropping an index is destructive and out of scope here.
    invoiceNumberUnique: uniqueIndex('invoices_invoice_number_unique').on(table.invoiceNumber),
    // Self-reference for credit notes
    parentInvoiceFk: index('invoices_parent_invoice_idx').on(table.parentInvoiceId),
    // Performance indexes
    orgStatusIdx: index('invoices_org_status_idx')
        .on(table.organizationId, table.status)
        .where(sql `${table.deletedAt} IS NULL`),
    interventionIdx: index('invoices_intervention_idx')
        .on(table.interventionId)
        .where(sql `${table.deletedAt} IS NULL`),
    sentAtIdx: index('invoices_sent_at_idx')
        .on(sql `${table.sentAt} DESC`)
        .where(sql `${table.deletedAt} IS NULL`),
    // CRITICAL: Performance indexes for overdue invoices
    overdueIdx: index('invoices_overdue_idx')
        .on(table.status, table.dueDate)
        .where(sql `${table.status} = 'sent' AND ${table.deletedAt} IS NULL`),
    interventionStatusIdx: index('invoices_intervention_status_idx')
        .on(table.interventionId, table.status, table.invoiceType)
        .where(sql `${table.deletedAt} IS NULL`),
    // Cross-org reconciliation lookup (#1280): preview/execute resolve pasted
    // client-invoice numbers GLOBALLY (numbers are globally unique), so the
    // filter carries no organization_id predicate and the
    // (organization_id, invoice_number) unique index can't serve it. Without a
    // bare invoice_number index a monthly 5–300 number batch scans the whole
    // live invoices table. Partial on deleted_at IS NULL to match the query.
    invoiceNumberIdx: index('invoices_invoice_number_idx')
        .on(table.invoiceNumber)
        .where(sql `${table.deletedAt} IS NULL`),
    // CRITICAL (#1211 security #1): at most one live commission invoice per
    // intervention. Cancelled/deleted rows are excluded so a reversed
    // commission frees the slot for a corrected one.
    oneCommissionPerInterventionIdx: uniqueIndex('invoices_one_commission_per_intervention_idx')
        .on(table.interventionId)
        .where(sql `${table.invoiceType} = 'commission' AND ${table.status} <> 'cancelled' AND ${table.deletedAt} IS NULL`),
    // CRITICAL: Credit note parent constraint
    creditNoteParentCheck: check('credit_note_parent_check', sql `
        (${table.invoiceType} = 'credit_note' AND ${table.parentInvoiceId} IS NOT NULL)
        OR (${table.invoiceType} != 'credit_note' AND ${table.parentInvoiceId} IS NULL)
      `),
    // CRITICAL: Global discount validation
    globalDiscountCheck: check('invoice_global_discount_check', sql `
        (${table.globalDiscountType} IS NULL AND ${table.globalDiscountValue} IS NULL)
        OR (${table.globalDiscountType} IS NOT NULL AND ${table.globalDiscountValue} IS NOT NULL)
      `),
}));
/**
 * Batch B2B payment reconciliation run (#1280). A SUPER_ADMIN pastes a month's
 * wire of client-invoice numbers; each is resolved + processed independently.
 *
 * Platform-level / cross-organization: invoice numbers are globally unique and a
 * single batch spans many tenants, so there is NO `organization_id` here (the
 * `invoice_sequences` / `platform_billing_entity` precedent). Per-tenant scope is
 * carried on each item row instead. Created only at execute time — preview is
 * stateless and never writes.
 */
export const paymentReconciliations = pgTable('payment_reconciliations', {
    id: uuid('id').primaryKey().defaultRandom(),
    label: varchar('label', { length: 255 }),
    // Async lifecycle (#1281): the batch is persisted, then processed out of the
    // request cycle. queued -> running -> completed (failed only on abort).
    status: varchar('status', { length: 20 })
        .$type()
        .notNull()
        .default(ReconciliationRunStatus.QUEUED),
    totalItems: integer('total_items').notNull().default(0),
    succeededCount: integer('succeeded_count').notNull().default(0),
    failedCount: integer('failed_count').notNull().default(0),
    skippedCount: integer('skipped_count').notNull().default(0),
    // Aggregate totals over processed items. precision 12 (vs invoices' 10) — a
    // batch sums hundreds of invoices.
    totalClientTtc: decimal('total_client_ttc', { precision: 12, scale: 2 })
        .notNull()
        .default('0'),
    totalCommissionTtc: decimal('total_commission_ttc', {
        precision: 12,
        scale: 2,
    })
        .notNull()
        .default('0'),
    totalNetPayout: decimal('total_net_payout', { precision: 12, scale: 2 })
        .notNull()
        .default('0'),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    createdBy: uuid('created_by').references(() => users.id),
    // When the worker picked the run up, and a liveness heartbeat bumped after
    // each item so a reaper can detect a run abandoned by a crashed instance.
    startedAt: timestamp('started_at', { withTimezone: true }),
    heartbeatAt: timestamp('heartbeat_at', { withTimezone: true }),
    executedAt: timestamp('executed_at', { withTimezone: true }),
});
/**
 * One pasted token of a reconciliation run (#1280) — its resolution and outcome.
 * Carries its own `organization_id` (the resolved tenant; null when introuvable),
 * so the cross-org parent stays tenant-agnostic while each row remains attributable.
 */
export const paymentReconciliationItems = pgTable('payment_reconciliation_items', {
    id: uuid('id').primaryKey().defaultRandom(),
    reconciliationId: uuid('reconciliation_id')
        .notNull()
        .references(() => paymentReconciliations.id),
    rawInput: varchar('raw_input', { length: 100 }).notNull(),
    // All nullable: an introuvable token resolves to nothing.
    invoiceId: uuid('invoice_id').references(() => invoices.id),
    organizationId: uuid('organization_id').references(() => organizations.id),
    interventionId: uuid('intervention_id').references(() => interventions.id),
    technicianId: uuid('technician_id').references(() => technicians.id),
    invoiceType: varchar('invoice_type', { length: 20 }).$type(),
    classification: varchar('classification', { length: 30 })
        .$type()
        .notNull(),
    outcome: varchar('outcome', { length: 20 }).$type(),
    // The generated commission invoice (CM-), when one was produced.
    commissionInvoiceId: uuid('commission_invoice_id').references(() => invoices.id),
    clientTtc: decimal('client_ttc', { precision: 10, scale: 2 }),
    commissionTtc: decimal('commission_ttc', { precision: 10, scale: 2 }),
    netPayout: decimal('net_payout', { precision: 10, scale: 2 }),
    errorMessage: text('error_message'),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, table => ({
    reconciliationIdx: index('payment_reconciliation_items_reconciliation_idx').on(table.reconciliationId),
    // A row that resolved to a tenant artefact (invoice/intervention) MUST carry
    // its organization_id — tenant audit data is never stored without a tenant
    // boundary. Only unresolved tokens (introuvable / mauvais type / ambigu)
    // may leave organization_id null (they reference nothing).
    orgWhenResolvedCheck: check('payment_reconciliation_items_org_when_resolved_check', sql `${table.organizationId} IS NOT NULL OR (${table.invoiceId} IS NULL AND ${table.interventionId} IS NULL)`),
}));
export const quotes = pgTable('quotes', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // Numbering
    quoteNumber: varchar('quote_number', { length: 50 }).notNull(),
    status: varchar('status', { length: 30 })
        .$type()
        .notNull()
        .default(QuoteStatus.PENDING),
    version: integer('version').notNull().default(1),
    // Relationships
    interventionId: uuid('intervention_id').references(() => interventions.id),
    technicianId: uuid('technician_id')
        .notNull()
        .references(() => technicians.id),
    parentQuoteId: uuid('parent_quote_id'), // Self-reference for revisions
    documentId: uuid('document_id').references(() => documents.id),
    // Snapshots (frozen at creation)
    issuerSnapshot: jsonb('issuer_snapshot').notNull(),
    clientSnapshot: jsonb('client_snapshot').notNull(),
    // NOTE: No technician_snapshot on quotes (prevents client bypass)
    // Financials
    subtotalHt: decimal('subtotal_ht', { precision: 10, scale: 2 })
        .notNull()
        .default('0'),
    globalDiscountType: varchar('global_discount_type', {
        length: 20,
    }).$type(),
    globalDiscountValue: decimal('global_discount_value', {
        precision: 10,
        scale: 2,
    }),
    globalDiscountHt: decimal('global_discount_ht', {
        precision: 10,
        scale: 2,
    }).default('0'),
    totalHt: decimal('total_ht', { precision: 10, scale: 2 })
        .notNull()
        .default('0'),
    totalVat: decimal('total_vat', { precision: 10, scale: 2 })
        .notNull()
        .default('0'),
    totalTtc: decimal('total_ttc', { precision: 10, scale: 2 })
        .notNull()
        .default('0'),
    vatBreakdown: jsonb('vat_breakdown').notNull().default([]),
    // Dates
    issueDate: date('issue_date').notNull().defaultNow(),
    validUntil: date('valid_until').notNull(),
    validatedAt: timestamp('validated_at', { withTimezone: true }),
    declinedAt: timestamp('declined_at', { withTimezone: true }),
    supersededAt: timestamp('superseded_at', { withTimezone: true }),
    // Curation (#1150): SUPER_ADMIN selects which quotes are presented to the
    // client. NULL = back-office-only; org ADMIN/FM RLS requires NOT NULL.
    sharedWithClientAt: timestamp('shared_with_client_at', {
        withTimezone: true,
    }),
    sharedBy: uuid('shared_by').references(() => users.id),
    /**
     * A HUMAN withdrew this quote from the client (#1586).
     *
     * `unshare()` clears `sharedWithClientAt` and leaves the quote PENDING, so
     * without this the automatic sweep cannot tell a deliberate withdrawal from
     * a quote that was never shared: it re-shares within two minutes and signs
     * on the client's behalf on the tick after — overriding the one explicit,
     * documented way a person says « this must not go out ».
     *
     * Terminal for automation of that quote, including a later manual re-share.
     * Deliberately blunt: once a person has intervened on a devis, stepping
     * back is the safe default, and re-arming the machine is a decision nobody
     * has asked for.
     *
     * NOT reused from `auto_decision_attempt`: pushing that to the cap would
     * fire the « abandoned, a human must decide » Sentry backstop for a
     * perfectly healthy human action.
     *
     * « Human » is exact by construction, not by convention: `unshare()` has a
     * single caller — `POST /quotes/:id/unshare`, `@Roles(SUPER_ADMIN)` — and it
     * runs on the RLS path, so it needs a live request's `accessToken`. No cron
     * or listener can reach it. Re-check that before adding a second caller.
     */
    manuallyUnsharedAt: timestamp('manually_unshared_at', {
        withTimezone: true,
    }),
    // Content
    description: text('description'),
    legalFooter: text('legal_footer').notNull(),
    note: text('note'),
    // Client decision feedback (#1188). Each revision is a new quote row, so the
    // outcome reason is a normalized, immutable attribute of that version.
    // revisionNotes: set on PENDING → REVISION_REQUESTED (client describes the
    // changes). declineReason: optional, set on PENDING → DECLINED.
    revisionNotes: text('revision_notes'),
    declineReason: text('decline_reason'),
    // Who took the client decision (validate / decline / request-revision),
    // completing the decision record so the UI can attribute it (#1189). Null
    // for quotes with no decision yet. The note above is the *why*; this is the
    // *who* — together with validated_at/declined_at (the *when*) they describe
    // the one decision event.
    decidedBy: uuid('decided_by').references(() => users.id),
    /**
     * THE AUTOMATION MARKER (#1816). Non-null means the engine took the
     * client's DECISION — it validated the quote with no human in the loop.
     *
     * SHARING DOES NOT SET IT: in `auto_share` mode the client still clicks, so
     * stamping it on the share would attribute hand-validated quotes to the
     * engine. An automated SHARE is `shared_by IS NULL` (the manual path always
     * names a SUPER_ADMIN) plus the score link.
     *
     * It exists because `decided_by` cannot carry the fact WITHOUT LYING: it is
     * a `users` FK, so an automated decision may either stamp a real id — and
     * the drawer renders « <Nom> a validé ce devis » for a decision nobody
     * took — or stay null, which is truthful but says nothing. So `decided_by`
     * stays NULL here and THIS column is what lets the UI say « Validé
     * automatiquement selon votre règle ».
     */
    autoDecidedAt: timestamp('auto_decided_at', { withTimezone: true }),
    /**
     * The #1585 score row this automated decision was taken on — the audit
     * trail « every automated decision traces back to its stored score and
     * weights version » asks for. An FK rather than copied numbers: the score
     * table is append-only, so the row it points at is exactly what the engine
     * saw, weights version included, and cannot drift.
     *
     * Nullable even on the auto path: a decision must never fail to record
     * itself because its explanation could not be resolved.
     */
    autoDecisionScoreId: uuid('auto_decision_score_id').references(() => quoteDecisionScores.id),
    /**
     * Failed automatic-decision attempts, so a permanently-broken quote is
     * abandoned instead of retried on every tick forever (#1817).
     *
     * NO LEASE COLUMN BESIDE IT, unlike `generationAttempt`/`generationLeaseAt`.
     * A lease answers « did the worker die mid-flight », and that question only
     * exists because a PDF render is long and leaves a `GENERATING` row behind.
     * An automatic decision is ONE short transaction: if the process dies it
     * rolls back, leaving nothing half-done, and `findByIdAndOrgForUpdate`'s row
     * lock already serializes two replicas on the same quote — the loser
     * re-reads a row that no longer passes its gates. So the only thing left to
     * track is failure, and this counts it.
     */
    autoDecisionAttempt: integer('auto_decision_attempt').notNull().default(0),
    // Audit — createdBy (users.id) is nullable only for technician-created
    // quotes (no Supabase session available in technician-api); those carry
    // createdByTechnicianId instead. quotes_attribution_check (below, applied
    // by migration 0103) enforces that at least one creator is set, so
    // attribution stays traceable (French legal). technicianId is the quote's
    // SUBJECT technician, not its creator — it cannot serve as attribution.
    createdAt: timestamp('created_at', { withTimezone: true }).defaultNow(),
    createdBy: uuid('created_by').references(() => users.id),
    createdByTechnicianId: uuid('created_by_technician_id').references(() => technicians.id),
    updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow(),
    updatedBy: uuid('updated_by').references(() => users.id),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    deletedBy: uuid('deleted_by').references(() => users.id),
    // PDF generation state (#1417 Phase 2). Orthogonal to `status` above — see
    // QuoteGenerationStatus doc comment. Two-tx create/revision flow: Tx A
    // reserves the number + row (GENERATING, contentRevision=1,
    // generationAttempt=1, renderSnapshot=frozen mapper input), the PDF
    // renders/uploads outside any tx, Tx B CAS-attaches the document and
    // flips READY (or writes FAILED on render/upload failure). See
    // quotes/AGENTS.md.
    generationStatus: varchar('generation_status', { length: 20 })
        .$type()
        .notNull()
        .default(QuoteGenerationStatus.READY),
    // Bumped by update() whenever line items/totals/content change; starts at
    // 1 for every newly-created row (a revision is a new row, not a bump).
    contentRevision: integer('content_revision').notNull().default(1),
    // Which contentRevision the currently-attached documentId corresponds to.
    // NULL until the first successful Tx B; share() requires this to equal
    // contentRevision.
    documentRevision: integer('document_revision'),
    // Monotonically bumped on every (re)generation attempt — Tx A on create,
    // manual regenerate (PR3), and the reaper (PR3) reclaiming a stale lease.
    // CAS discriminant so a stale/losing attempt's Tx B can't clobber a newer one.
    generationAttempt: integer('generation_attempt').notNull().default(1),
    // Set when an attempt starts (GENERATING); the PR3 reaper reads this to
    // find stale leases.
    generationLeaseAt: timestamp('generation_lease_at', {
        withTimezone: true,
    }),
    // Sanitized {message, code?, at} on FAILED — never a raw stack trace.
    generationError: jsonb('generation_error'),
    // NOTE: no render_snapshot column here — moved to the separate
    // `quoteRenderSnapshots` table below (#1417 Phase 2 automated-review
    // fix). See that table's doc comment for why.
    // Optional client-/caller-supplied idempotency key for create/createRevision.
    idempotencyKey: varchar('idempotency_key', { length: 255 }),
}, table => ({
    // Unique quote number per organization
    numberUnique: unique('quotes_number_unique').on(table.organizationId, table.quoteNumber),
    // #1417 Phase 2: dedup concurrent/retried create-with-idempotency-key
    // requests. Partial (only when a key was actually supplied) and per-org,
    // not per-user — a second user in the same org retrying the same logical
    // request is out of scope. AND deleted_at IS NULL (automated-review fix):
    // without it, soft-deleting a quote leaves its key still occupying the
    // index, so a later request reusing that key hits onConflictDoNothing
    // against a row `findByOrgAndIdempotencyKey*` (which filters
    // isNull(deletedAt)) can never find — createGenerating's conflict-recovery
    // re-select then finds nothing and throws a 500. The ON CONFLICT target in
    // `createGenerating` (quotes.repository.ts) must use this EXACT predicate
    // or Postgres won't match it to this index.
    orgIdempotencyKeyUnique: uniqueIndex('quotes_org_idempotency_key_unique')
        .on(table.organizationId, table.idempotencyKey)
        .where(sql `${table.idempotencyKey} IS NOT NULL AND ${table.deletedAt} IS NULL`),
    generationStatusCheck: check('quotes_generation_status_check', sql `${table.generationStatus} IN ('GENERATING', 'READY', 'FAILED')`),
    // Self-reference for revisions
    parentQuoteFk: index('quotes_parent_idx')
        .on(table.parentQuoteId)
        .where(sql `${table.parentQuoteId} IS NOT NULL`),
    // Performance indexes
    orgStatusIdx: index('quotes_org_status_idx')
        .on(table.organizationId, table.status)
        .where(sql `${table.deletedAt} IS NULL`),
    interventionIdx: index('quotes_intervention_idx')
        .on(table.interventionId)
        .where(sql `${table.deletedAt} IS NULL`),
    technicianIdx: index('quotes_technician_idx')
        .on(table.technicianId)
        .where(sql `${table.deletedAt} IS NULL`),
    // CRITICAL: Performance index for validated quotes
    interventionStatusIdx: index('quotes_intervention_status_idx')
        .on(table.interventionId, table.status)
        .where(sql `${table.deletedAt} IS NULL`),
    // CRITICAL: Global discount validation
    globalDiscountCheck: check('quote_global_discount_check', sql `
        (${table.globalDiscountType} IS NULL AND ${table.globalDiscountValue} IS NULL)
        OR (${table.globalDiscountType} IS NOT NULL AND ${table.globalDiscountValue} IS NOT NULL)
      `),
    // CRITICAL: Every quote must have a traceable creator — a back-office
    // user OR a technician (technician-api path). French legal requirement.
    attributionCheck: check('quotes_attribution_check', sql `${table.createdBy} IS NOT NULL OR ${table.createdByTechnicianId} IS NOT NULL`),
}));
/**
 * Frozen `mapQuoteToPdfData(...)` render input for a quote's PDF, one row
 * per quote (#1417 Phase 2 automated-review fix). Moved OFF the `quotes`
 * table entirely — the REST API path was already clean (never on
 * `QuoteResponseDto`), but `quotes` also carries a live Supabase Realtime
 * subscription (`apps/web/src/hooks/quotes/useRealtimeQuotes.ts`,
 * `RealtimeTable.QUOTES` / `RealtimeEvent.ALL`) gated by the SAME RLS SELECT
 * policies as any ordinary SELECT — meaning ADMIN/FACILITY_MANAGER/VIEWER.
 * Postgres logical-replication-backed Realtime sends the FULL ROW over the
 * websocket regardless of what the client code does with the payload, so a
 * `render_snapshot` column on `quotes` would leak the technician's company
 * name/address/SIRET/VAT to any of those roles inspecting the websocket —
 * a bypass the REST-API-only reading of quotes/AGENTS.md missed.
 *
 * System table, not tenant data: no RLS policies (see rls-classification.ts
 * NO_RLS_SERVICE_ROLE, `storage_cleanup_intents` precedent) and no Realtime
 * subscription — written/read only via `drizzleService.admin` /
 * `QuotesRepository`. Upserted whenever a fresh snapshot is captured (Tx A on
 * create/createRevision/createFromTechnicianInput; later PR3's regenerate).
 */
export const quoteRenderSnapshots = pgTable('quote_render_snapshots', {
    quoteId: uuid('quote_id')
        .primaryKey()
        .references(() => quotes.id, { onDelete: 'cascade' }),
    renderSnapshot: jsonb('render_snapshot').notNull(),
    // #1417 Phase 2 skeptic fix: which `quotes.contentRevision` THIS snapshot
    // was captured at. Required so `_renderUploadAndAttach`'s CAS baseline
    // (`contentRevisionAtStart`) can be read from the snapshot itself instead
    // of the passed-in quote row's CURRENT contentRevision — for the reaper's
    // resume path those two can differ (a content edit can bump the row's
    // contentRevision without touching this table), and using the row's own
    // current value there would let a stale snapshot's CAS trivially match and
    // get silently certified as fresh. See quotes.service.ts's doc comment.
    // Defaulted to 1 to match `quotes.contentRevision` and to keep the ALTER that
    // adds this column expand-safe: it lands on a table the previous release
    // already writes to, and that release does not set this column.
    contentRevision: integer('content_revision').notNull().default(1),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
});
/**
 * Devis decision-engine scores (#1585) — append-only.
 *
 * One row per SCORING, never one per quote: a stored score is NEVER overwritten
 * in place. Re-scoring the same quote under a newer `weightsVersion` inserts a
 * NEW row, so the explanation shown to a client at the time of a decision stays
 * byte-identical forever. Read the latest by `scoredAt DESC, id DESC`.
 *
 * Tenant table (`organization_id NOT NULL`, soft delete, RLS). Written only by
 * `QuoteDecisionListener` on the service-role admin client (an event listener
 * has no request context), which is why `authenticated` holds SELECT only —
 * see `apps/api/src/drizzle/rls-classification.ts` `COMMAND_EXEMPTIONS`.
 */
export const quoteDecisionScores = pgTable('quote_decision_scores', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // ON DELETE cascade: a score is derived data about one quote and is
    // meaningless without it, so the FK guards against an orphan, not against
    // erasing the parent. Mirrors `quoteRenderSnapshots`, the only other child
    // of `quotes`. Tenant code soft-deletes (golden rule 4), so this fires only
    // where a quote is truly erased — the demo wipe and test fixtures. The
    // other FKs stay `no action`; see migration 0170's header.
    quoteId: uuid('quote_id')
        .notNull()
        .references(() => quotes.id, { onDelete: 'cascade' }),
    // Nullable: a quote can exist without an intervention (QuoteCreatedEvent's
    // own `interventionId` is optional).
    interventionId: uuid('intervention_id').references(() => interventions.id),
    technicianId: uuid('technician_id')
        .notNull()
        .references(() => technicians.id),
    /** 0..100. Decimal because the weighted sum is not an integer. */
    score: decimal('score', { precision: 5, scale: 2 }).notNull(),
    /** Stage A vetoes that fired; empty when none did. */
    vetoes: text('vetoes')
        .array()
        .$type()
        .notNull()
        .default(sql `'{}'::text[]`),
    /** Full per-criterion explanation — the reason this table exists. */
    breakdown: jsonb('breakdown').$type().notNull(),
    /** Stage A + the sole-PENDING rule, resolved at scoring time. */
    autoDecidable: boolean('auto_decidable').notNull(),
    /** Which weight set produced `score`/`breakdown`. */
    weightsVersion: integer('weights_version').notNull(),
    scoredAt: timestamp('scored_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    ...auditTimestamps,
}, table => [
    index('quote_decision_scores_quote_idx').on(table.quoteId, table.scoredAt),
    index('quote_decision_scores_org_idx').on(table.organizationId, table.scoredAt),
    check('quote_decision_scores_score_range', sql `${table.score} >= 0 AND ${table.score} <= 100`),
]);
/**
 * An organization's contract price list (#2670, slice #2964) — today only
 * Verisure's « Service Serrure » prestations (A15 / A27). The invoice editor
 * prefills a line from an item; nothing here is binding on the invoice (A21).
 *
 * SUPER_ADMIN-only, like `invoices`: these are the prices the platform bills
 * the organization, not data the organization edits. RLS does not confine a
 * SUPER_ADMIN to one org, so every reader also filters `organization_id`.
 * Soft delete only — `authenticated` holds no DELETE (COMMAND_EXEMPTIONS).
 */
export const organizationPriceItems = pgTable('organization_price_items', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // The `OrganizationService.code` the item is priced under (#2665); NULL in
    // an org without services. The prefill offers an intervention only the
    // items of its own service.
    serviceCode: varchar('service_code', { length: 50 }),
    // The prestation's stable code — the Serrure equipment row's `catalog_key`
    // (`serrure-<code>`, docs/decisions/0003-intervention-service.md §7).
    code: varchar('code', { length: 100 }).notNull(),
    label: text('label').notNull(),
    // NULL only for a « Sur devis uniquement » item, which is priced by a quote.
    basePriceHt: decimal('base_price_ht', { precision: 10, scale: 2 }),
    quoteOnly: boolean('quote_only').notNull().default(false),
    isActive: boolean('is_active').notNull().default(true),
    ...auditTimestamps,
}, table => [
    uniqueIndex('organization_price_items_code_idx')
        .on(table.organizationId, table.code)
        .where(sql `${table.deletedAt} IS NULL`),
    // Every arm spelled out: `(a IS NULL) OR (a >= 0)` alone would accept a
    // priced item with no price (#2723).
    check('organization_price_items_price', sql `(${table.quoteOnly} AND ${table.basePriceHt} IS NULL) OR (NOT ${table.quoteOnly} AND ${table.basePriceHt} IS NOT NULL AND ${table.basePriceHt} >= 0)`),
]);
// Line Items (Polymorphic - Shared by Invoices & Quotes)
export const lineItems = pgTable('line_items', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id')
        .notNull()
        .references(() => organizations.id),
    // Polymorphic reference
    entityType: varchar('entity_type', { length: 20 })
        .notNull()
        .$type(),
    entityId: uuid('entity_id').notNull(),
    // Content
    displayOrder: integer('display_order').notNull().default(0),
    designation: text('designation').notNull(),
    description: text('description'),
    // Pricing
    quantity: decimal('quantity', { precision: 10, scale: 3 }).notNull(),
    unitPriceHt: decimal('unit_price_ht', {
        precision: 10,
        scale: 2,
    }).notNull(),
    vatRate: decimal('vat_rate', { precision: 5, scale: 2 }).notNull(),
    // Discounts
    discountType: varchar('discount_type', {
        length: 20,
    }).$type(),
    discountValue: decimal('discount_value', { precision: 10, scale: 2 }),
    // Computed totals
    lineTotalHt: decimal('line_total_ht', {
        precision: 10,
        scale: 2,
    }).notNull(),
    lineVatAmount: decimal('line_vat_amount', {
        precision: 10,
        scale: 2,
    }).notNull(),
    lineTotalTtc: decimal('line_total_ttc', {
        precision: 10,
        scale: 2,
    }).notNull(),
    // Audit
    createdBy: uuid('created_by').references(() => users.id),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedBy: uuid('updated_by').references(() => users.id),
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, table => ({
    // Composite unique constraint
    orgEntityUnique: unique('line_items_org_entity_idx').on(table.organizationId, table.entityType, table.entityId, table.id),
    // Performance indexes
    entityIdx: index('line_items_entity_idx').on(table.entityType, table.entityId),
    orgIdx: index('line_items_org_idx').on(table.organizationId),
    // Entity type validation
    entityTypeCheck: check('line_items_entity_type_check', sql `${table.entityType} IN ('invoice', 'quote')`),
}));
// Relations
export const organizationRelations = relations(organizations, ({ many, one }) => ({
    users: many(users),
    sites: many(sites),
    equipmentTemplates: many(equipment),
    equipmentSites: many(equipmentSites),
    siteContacts: many(siteContacts),
    documents: many(documents),
    interventions: many(interventions),
    activityLogs: many(activityLogs),
    entityDocuments: many(entityDocuments, {
        relationName: 'linkedOrganization',
    }),
    createdByUser: one(users, {
        fields: [organizations.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [organizations.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [organizations.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
    invoiceUser: one(users, {
        fields: [organizations.invoiceUserId],
        references: [users.id],
        relationName: 'invoiceUser',
    }),
}));
export const userRelations = relations(users, ({ one, many }) => ({
    organization: one(organizations, {
        fields: [users.organizationId],
        references: [organizations.id],
    }),
    createdByUser: one(users, {
        fields: [users.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [users.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [users.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
    // Inverse relations for audit fields where this user is the creator/updater/deleter
    createdOrganizations: many(organizations, { relationName: 'creator' }),
    updatedOrganizations: many(organizations, { relationName: 'updater' }),
    deletedOrganizations: many(organizations, { relationName: 'deleter' }),
    createdSites: many(sites, { relationName: 'creator' }),
    updatedSites: many(sites, { relationName: 'updater' }),
    deletedSites: many(sites, { relationName: 'deleter' }),
    createdEquipment: many(equipment, { relationName: 'creator' }),
    updatedEquipment: many(equipment, { relationName: 'updater' }),
    deletedEquipment: many(equipment, { relationName: 'deleter' }),
    createdEquipmentSites: many(equipmentSites, { relationName: 'creator' }),
    updatedEquipmentSites: many(equipmentSites, { relationName: 'updater' }),
    deletedEquipmentSites: many(equipmentSites, { relationName: 'deleter' }),
    createdSiteContacts: many(siteContacts, { relationName: 'creator' }),
    updatedSiteContacts: many(siteContacts, { relationName: 'updater' }),
    deletedSiteContacts: many(siteContacts, { relationName: 'deleter' }),
    createdUserSites: many(siteContacts, { relationName: 'creator' }),
    updatedUserSites: many(siteContacts, { relationName: 'updater' }),
    deletedUserSites: many(siteContacts, { relationName: 'deleter' }),
    createdDocuments: many(documents, { relationName: 'creator' }),
    updatedDocuments: many(documents, { relationName: 'updater' }),
    deletedDocuments: many(documents, { relationName: 'deleter' }),
    createdEntityDocuments: many(entityDocuments, { relationName: 'creator' }),
    updatedEntityDocuments: many(entityDocuments, { relationName: 'updater' }),
    deletedEntityDocuments: many(entityDocuments, { relationName: 'deleter' }),
    createdInterventions: many(interventions, { relationName: 'creator' }),
    updatedInterventions: many(interventions, { relationName: 'updater' }),
    deletedInterventions: many(interventions, { relationName: 'deleter' }),
    activityLogs: many(activityLogs, { relationName: 'actor' }),
    createdTechnicians: many(technicians, { relationName: 'creator' }),
    updatedTechnicians: many(technicians, { relationName: 'updater' }),
    deletedTechnicians: many(technicians, { relationName: 'deleter' }),
    createdTechnicianWorkZones: many(technicianWorkZones, {
        relationName: 'creator',
    }),
    updatedTechnicianWorkZones: many(technicianWorkZones, {
        relationName: 'updater',
    }),
    deletedTechnicianWorkZones: many(technicianWorkZones, {
        relationName: 'deleter',
    }),
    createdTechnicianAvailability: many(technicianAvailability, {
        relationName: 'creator',
    }),
    updatedTechnicianAvailability: many(technicianAvailability, {
        relationName: 'updater',
    }),
    deletedTechnicianAvailability: many(technicianAvailability, {
        relationName: 'deleter',
    }),
    createdTechnicianContacts: many(technicianContacts, {
        relationName: 'creator',
    }),
    updatedTechnicianContacts: many(technicianContacts, {
        relationName: 'updater',
    }),
    deletedTechnicianContacts: many(technicianContacts, {
        relationName: 'deleter',
    }),
    setTechnicianFlags: many(technicianFlags, { relationName: 'flagSetter' }),
    removedTechnicianFlags: many(technicianFlags, {
        relationName: 'flagRemover',
    }),
    // Specific relations
    invoiceForOrganizations: many(organizations, { relationName: 'invoiceUser' }),
    invoiceForSites: many(sites, { relationName: 'invoiceUser' }),
    grantedUserSites: many(siteContacts, { relationName: 'granter' }),
    revokedUserSites: many(siteContacts, { relationName: 'revoker' }),
    siteContacts: many(siteContacts, { relationName: 'user' }), // Link to site_contacts where this user is the contact
}));
export const siteRelations = relations(sites, ({ one, many }) => ({
    organization: one(organizations, {
        fields: [sites.organizationId],
        references: [organizations.id],
    }),
    invoiceUser: one(users, {
        fields: [sites.invoiceUserId],
        references: [users.id],
        relationName: 'invoiceUser',
    }),
    createdByUser: one(users, {
        fields: [sites.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [sites.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [sites.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
    equipmentSites: many(equipmentSites),
    siteContacts: many(siteContacts),
    interventions: many(interventions),
    entityDocuments: many(entityDocuments, { relationName: 'linkedSite' }),
}));
export const equipmentRelations = relations(equipment, ({ one, many }) => ({
    organization: one(organizations, {
        fields: [equipment.organizationId],
        references: [organizations.id],
    }),
    createdByUser: one(users, {
        fields: [equipment.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [equipment.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [equipment.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
    equipmentSites: many(equipmentSites),
    entityDocuments: many(entityDocuments, { relationName: 'linkedEquipment' }),
}));
export const equipmentSitesRelations = relations(equipmentSites, ({ one }) => ({
    organization: one(organizations, {
        fields: [equipmentSites.organizationId],
        references: [organizations.id],
    }),
    equipment: one(equipment, {
        fields: [equipmentSites.equipmentId],
        references: [equipment.id],
    }),
    site: one(sites, {
        fields: [equipmentSites.siteId],
        references: [sites.id],
    }),
    createdByUser: one(users, {
        fields: [equipmentSites.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [equipmentSites.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [equipmentSites.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
}));
export const siteContactsRelations = relations(siteContacts, ({ one }) => ({
    organization: one(organizations, {
        fields: [siteContacts.organizationId],
        references: [organizations.id],
    }),
    site: one(sites, {
        fields: [siteContacts.siteId],
        references: [sites.id],
    }),
    user: one(users, {
        fields: [siteContacts.userId],
        references: [users.id],
    }),
    createdByUser: one(users, {
        fields: [siteContacts.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [siteContacts.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [siteContacts.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
}));
export const documentsRelations = relations(documents, ({ one, many }) => ({
    organization: one(organizations, {
        fields: [documents.organizationId],
        references: [organizations.id],
    }),
    // Self-referencing relation for parent document (previous version)
    parentDocument: one(documents, {
        fields: [documents.parentDocumentId],
        references: [documents.id],
        relationName: 'parentDocument',
    }),
    // Self-referencing relation for child documents (newer versions)
    childDocuments: many(documents, {
        relationName: 'parentDocument', // This links back to the 'parentDocument' relation on the other side
    }),
    createdByUser: one(users, {
        fields: [documents.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [documents.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [documents.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
    entityDocuments: many(entityDocuments, { relationName: 'document' }),
}));
export const entityDocumentsRelations = relations(entityDocuments, ({ one }) => ({
    organization: one(organizations, {
        fields: [entityDocuments.organizationId],
        references: [organizations.id],
        relationName: 'organization',
    }),
    document: one(documents, {
        fields: [entityDocuments.documentId],
        references: [documents.id],
        relationName: 'document',
    }),
    createdByUser: one(users, {
        fields: [entityDocuments.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [entityDocuments.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [entityDocuments.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
    // Polymorphic relations - conditional 'one' relations for each entity type
    linkedOrganization: one(organizations, {
        fields: [entityDocuments.entityId],
        references: [organizations.id],
        relationName: 'linkedOrganization',
    }),
    linkedSite: one(sites, {
        fields: [entityDocuments.entityId],
        references: [sites.id],
        relationName: 'linkedSite',
    }),
    linkedEquipment: one(equipment, {
        fields: [entityDocuments.entityId],
        references: [equipment.id],
        relationName: 'linkedEquipment',
    }),
}));
export const notesRelations = relations(notes, ({ one, many }) => ({
    organization: one(organizations, {
        fields: [notes.organizationId],
        references: [organizations.id],
        relationName: 'organization',
    }),
    author: one(users, {
        fields: [notes.authorId],
        references: [users.id],
        relationName: 'author',
    }),
    createdByUser: one(users, {
        fields: [notes.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [notes.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [notes.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
    parent: one(notes, {
        fields: [notes.parentId],
        references: [notes.id],
        relationName: 'parentNote',
    }),
    replies: many(notes, {
        relationName: 'parentNote',
    }),
    recipients: many(noteRecipients),
}));
export const noteRecipientsRelations = relations(noteRecipients, ({ one }) => ({
    organization: one(organizations, {
        fields: [noteRecipients.organizationId],
        references: [organizations.id],
    }),
    note: one(notes, {
        fields: [noteRecipients.noteId],
        references: [notes.id],
    }),
    user: one(users, {
        fields: [noteRecipients.userId],
        references: [users.id],
    }),
}));
export const interventionsRelations = relations(interventions, ({ one, many }) => ({
    organization: one(organizations, {
        fields: [interventions.organizationId],
        references: [organizations.id],
    }),
    site: one(sites, {
        fields: [interventions.siteId],
        references: [sites.id],
    }),
    // `equipment_id` targets `equipment.id` (migration
    // 0040_intervention_equipmentId), not `equipment_sites.id` (#1484).
    equipment: one(equipment, {
        fields: [interventions.equipmentId],
        references: [equipment.id],
    }),
    technician: one(technicians, {
        fields: [interventions.technicianId],
        references: [technicians.id],
    }),
    createdByUser: one(users, {
        fields: [interventions.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [interventions.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [interventions.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
    technicianActions: many(technicianInterventionActions),
    reports: many(interventionReports),
}));
export const interventionReportsRelations = relations(interventionReports, ({ one }) => ({
    organization: one(organizations, {
        fields: [interventionReports.organizationId],
        references: [organizations.id],
    }),
    intervention: one(interventions, {
        fields: [interventionReports.interventionId],
        references: [interventions.id],
    }),
    createdByUser: one(users, {
        fields: [interventionReports.createdBy],
        references: [users.id],
        relationName: 'reportCreator',
    }),
    updatedByUser: one(users, {
        fields: [interventionReports.updatedBy],
        references: [users.id],
        relationName: 'reportUpdater',
    }),
    deletedByUser: one(users, {
        fields: [interventionReports.deletedBy],
        references: [users.id],
        relationName: 'reportDeleter',
    }),
}));
export const activityLogsRelations = relations(activityLogs, ({ one }) => ({
    organization: one(organizations, {
        fields: [activityLogs.organizationId],
        references: [organizations.id],
    }),
    actor: one(users, {
        fields: [activityLogs.actorId],
        references: [users.id],
        relationName: 'actor',
    }),
    actorTechnician: one(technicians, {
        fields: [activityLogs.actorTechnicianId],
        references: [technicians.id],
        relationName: 'actorTechnician',
    }),
}));
export const userNotificationsRelations = relations(userNotifications, ({ one }) => ({
    organization: one(organizations, {
        fields: [userNotifications.organizationId],
        references: [organizations.id],
    }),
    user: one(users, {
        fields: [userNotifications.userId],
        references: [users.id],
        relationName: 'notificationRecipient',
    }),
    actor: one(users, {
        fields: [userNotifications.actorId],
        references: [users.id],
        relationName: 'notificationActor',
    }),
}));
export const techniciansRelations = relations(technicians, ({ one, many }) => ({
    billingContact: one(technicianContacts, {
        fields: [technicians.billingContactId],
        references: [technicianContacts.id],
        relationName: 'billingContact',
    }),
    createdByUser: one(users, {
        fields: [technicians.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [technicians.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [technicians.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
    workZones: many(technicianWorkZones),
    availability: many(technicianAvailability),
    contacts: many(technicianContacts, { relationName: 'technician' }),
    flags: many(technicianFlags),
    interventions: many(interventions),
    interventionActions: many(technicianInterventionActions),
}));
export const technicianWorkZonesRelations = relations(technicianWorkZones, ({ one }) => ({
    technician: one(technicians, {
        fields: [technicianWorkZones.technicianId],
        references: [technicians.id],
    }),
    createdByUser: one(users, {
        fields: [technicianWorkZones.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [technicianWorkZones.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [technicianWorkZones.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
}));
export const technicianAvailabilityRelations = relations(technicianAvailability, ({ one }) => ({
    technician: one(technicians, {
        fields: [technicianAvailability.technicianId],
        references: [technicians.id],
    }),
    createdByUser: one(users, {
        fields: [technicianAvailability.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [technicianAvailability.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [technicianAvailability.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
}));
export const technicianContactsRelations = relations(technicianContacts, ({ one, many }) => ({
    technician: one(technicians, {
        fields: [technicianContacts.technicianId],
        references: [technicians.id],
        relationName: 'technician',
    }),
    billingForTechnicians: many(technicians, {
        relationName: 'billingContact',
    }),
    createdByUser: one(users, {
        fields: [technicianContacts.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [technicianContacts.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [technicianContacts.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
}));
export const technicianFlagsRelations = relations(technicianFlags, ({ one }) => ({
    technician: one(technicians, {
        fields: [technicianFlags.technicianId],
        references: [technicians.id],
    }),
    setByUser: one(users, {
        fields: [technicianFlags.setBy],
        references: [users.id],
        relationName: 'flagSetter',
    }),
    createdByUser: one(users, {
        fields: [technicianFlags.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [technicianFlags.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [technicianFlags.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
}));
export const technicianInterventionActionsRelations = relations(technicianInterventionActions, ({ one }) => ({
    technician: one(technicians, {
        fields: [technicianInterventionActions.technicianId],
        references: [technicians.id],
    }),
    intervention: one(interventions, {
        fields: [technicianInterventionActions.interventionId],
        references: [interventions.id],
    }),
}));
// Platform Billing Entity Relations
export const platformBillingEntityRelations = relations(platformBillingEntity, ({ one }) => ({
    updatedByUser: one(users, {
        fields: [platformBillingEntity.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
}));
// Invoice Relations
export const invoicesRelations = relations(invoices, ({ one, many }) => ({
    organization: one(organizations, {
        fields: [invoices.organizationId],
        references: [organizations.id],
    }),
    intervention: one(interventions, {
        fields: [invoices.interventionId],
        references: [interventions.id],
    }),
    technician: one(technicians, {
        fields: [invoices.technicianId],
        references: [technicians.id],
    }),
    parentInvoice: one(invoices, {
        fields: [invoices.parentInvoiceId],
        references: [invoices.id],
        relationName: 'creditNotes',
    }),
    creditNotes: many(invoices, { relationName: 'creditNotes' }),
    document: one(documents, {
        fields: [invoices.documentId],
        references: [documents.id],
    }),
    lineItems: many(lineItems, { relationName: 'invoiceLineItems' }),
    createdByUser: one(users, {
        fields: [invoices.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [invoices.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [invoices.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
}));
// Quote Relations
export const quotesRelations = relations(quotes, ({ one, many }) => ({
    organization: one(organizations, {
        fields: [quotes.organizationId],
        references: [organizations.id],
    }),
    intervention: one(interventions, {
        fields: [quotes.interventionId],
        references: [interventions.id],
    }),
    technician: one(technicians, {
        fields: [quotes.technicianId],
        references: [technicians.id],
    }),
    parentQuote: one(quotes, {
        fields: [quotes.parentQuoteId],
        references: [quotes.id],
        relationName: 'revisions',
    }),
    revisions: many(quotes, { relationName: 'revisions' }),
    document: one(documents, {
        fields: [quotes.documentId],
        references: [documents.id],
    }),
    lineItems: many(lineItems, { relationName: 'quoteLineItems' }),
    createdByUser: one(users, {
        fields: [quotes.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [quotes.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
    deletedByUser: one(users, {
        fields: [quotes.deletedBy],
        references: [users.id],
        relationName: 'deleter',
    }),
}));
// Line Items Relations
export const lineItemsRelations = relations(lineItems, ({ one }) => ({
    organization: one(organizations, {
        fields: [lineItems.organizationId],
        references: [organizations.id],
    }),
    createdByUser: one(users, {
        fields: [lineItems.createdBy],
        references: [users.id],
        relationName: 'creator',
    }),
    updatedByUser: one(users, {
        fields: [lineItems.updatedBy],
        references: [users.id],
        relationName: 'updater',
    }),
}));
// ------------------- RLS ----------------------
// Reusable SQL Snippets for RLS Context
const currentUserRole = sql `get_user_role()`;
const currentUserOrgId = sql `get_user_organization_id()`;
const hasSiteAccess = sql `has_site_access(site_id)`;
const hasPrimarySiteAccess = sql `is_primary_contact(id)`;
const currentUserId = sql `get_current_user_id()`;
const isSuperAdmin = sql `${currentUserRole} = ${sql.raw(`'${UserRole.SUPER_ADMIN}'::"UserRole"`)}`;
const hasAdminOrgAccess = sql `${currentUserRole} = ${sql.raw(`'${UserRole.ADMIN}'::"UserRole"`)} AND organization_id = ${currentUserOrgId}`;
const hasFMOrgAccess = sql `${currentUserRole} = ${sql.raw(`'${UserRole.FACILITY_MANAGER}'::"UserRole"`)} AND organization_id = ${currentUserOrgId}`;
const hasViewerOrgAccess = sql `${currentUserRole} = ${sql.raw(`'${UserRole.VIEWER}'::"UserRole"`)} AND organization_id = ${currentUserOrgId}`;
const isCurrentUserProfile = sql `id = ${currentUserId}`;
const isPublicNote = sql `audience = ${sql.raw(`'${NoteAudience.ALL}'::"NoteAudience"`)}`;
// Policy for Organizations
export const organizationsSelectRLS = pgPolicy('organizations_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR (${currentUserRole} = ${sql.raw(`'${UserRole.ADMIN}'::"UserRole"`)} AND id = ${currentUserOrgId})
    OR (${currentUserRole} = ${sql.raw(`'${UserRole.FACILITY_MANAGER}'::"UserRole"`)} AND id = ${currentUserOrgId})
    OR (${currentUserRole} = ${sql.raw(`'${UserRole.VIEWER}'::"UserRole"`)} AND id = ${currentUserOrgId})
  `,
}).link(organizations);
export const organizationsInsertRLS = pgPolicy('organizations_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(organizations);
export const organizationsUpdateRLS = pgPolicy('organizations_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR (${currentUserRole} = ${sql.raw(`'${UserRole.ADMIN}'::"UserRole"`)} AND id = ${currentUserOrgId})
  `,
    withCheck: sql `
    ${isSuperAdmin}
    OR (${currentUserRole} = ${sql.raw(`'${UserRole.ADMIN}'::"UserRole"`)} AND id = ${currentUserOrgId})
  `,
}).link(organizations);
export const organizationsDeleteRLS = pgPolicy('organizations_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(organizations);
// Policy for Sites
export const sitesSelectRLS = pgPolicy('sites_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (
      (${currentUserRole} = ${sql.raw(`'${UserRole.FACILITY_MANAGER}'::"UserRole"`)} OR ${currentUserRole} = ${sql.raw(`'${UserRole.VIEWER}'::"UserRole"`)}
      ) AND has_site_access(id)
    )
  `,
}).link(sites);
export const sitesInsertRLS = pgPolicy('sites_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(sites);
export const sitesUpdateRLS = pgPolicy('sites_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (
      ${hasFMOrgAccess} AND has_site_access(id)
    )
    OR (
      ${hasViewerOrgAccess} AND ${hasPrimarySiteAccess}
    )
  `,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (
      ${hasFMOrgAccess} AND has_site_access(id)
    )
    OR (
      ${hasViewerOrgAccess} AND ${hasPrimarySiteAccess}
    )
  `,
}).link(sites);
export const sitesDeleteRLS = pgPolicy('sites_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(sites);
// Policy for Users
export const usersSelectRLS = pgPolicy('users_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR (${currentUserRole} = ${sql.raw(`'${UserRole.VIEWER}'::"UserRole"`)} AND ${isCurrentUserProfile})
  `,
}).link(users);
export const usersInsertRLS = pgPolicy('users_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(users);
export const usersUpdateRLS = pgPolicy('users_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR (${currentUserRole} = ${sql.raw(`'${UserRole.VIEWER}'::"UserRole"`)} AND ${isCurrentUserProfile})
  `,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR (${currentUserRole} = ${sql.raw(`'${UserRole.VIEWER}'::"UserRole"`)} AND ${isCurrentUserProfile})
  `,
}).link(users);
export const usersDeleteRLS = pgPolicy('users_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(users);
// Policy for Equipment (Templates - based on organization_id)
export const equipmentSelectRLS = pgPolicy('equipment_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR ${hasViewerOrgAccess}
  `,
}).link(equipment);
export const equipmentInsertRLS = pgPolicy('equipment_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(equipment);
export const equipmentUpdateRLS = pgPolicy('equipment_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(equipment);
export const equipmentDeleteRLS = pgPolicy('equipment_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(equipment);
// Policy for Equipment Sites (Instances - based on site_id)
export const equipmentSitesSelectRLS = pgPolicy('equipment_sites_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (${hasFMOrgAccess} AND ${hasSiteAccess})
    OR (${hasViewerOrgAccess} AND ${hasSiteAccess})
  `,
}).link(equipmentSites);
export const equipmentSitesInsertRLS = pgPolicy('equipment_sites_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (${hasFMOrgAccess} AND ${hasSiteAccess})
    OR (${hasViewerOrgAccess} AND ${hasSiteAccess})
  `,
}).link(equipmentSites);
export const equipmentSitesUpdateRLS = pgPolicy('equipment_sites_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (${hasFMOrgAccess} AND ${hasSiteAccess})
    OR (${hasViewerOrgAccess} AND ${hasSiteAccess})
  `,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (${hasFMOrgAccess} AND ${hasSiteAccess})
    OR (${hasViewerOrgAccess} AND ${hasSiteAccess})
  `,
}).link(equipmentSites);
export const equipmentSitesDeleteRLS = pgPolicy('equipment_sites_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (${hasFMOrgAccess} AND ${hasSiteAccess})
    OR (${hasViewerOrgAccess} AND ${hasSiteAccess})
  `,
}).link(equipmentSites);
// Policy for Site Contacts
export const siteContactsSelectRLS = pgPolicy('site_contacts_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (${hasFMOrgAccess} AND ${hasSiteAccess})
    OR (${hasViewerOrgAccess} AND ${hasSiteAccess})
  `,
}).link(siteContacts);
export const siteContactsInsertRLS = pgPolicy('site_contacts_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (${hasFMOrgAccess} AND ${hasSiteAccess})
  `,
}).link(siteContacts);
export const siteContactsUpdateRLS = pgPolicy('site_contacts_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (${hasFMOrgAccess} AND ${hasSiteAccess})
    OR (${hasViewerOrgAccess} AND ${hasSiteAccess})
  `,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (${hasFMOrgAccess} AND ${hasSiteAccess})
    OR (${hasViewerOrgAccess} AND ${hasSiteAccess})
  `,
}).link(siteContacts);
export const siteContactsDeleteRLS = pgPolicy('site_contacts_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR (${hasFMOrgAccess} AND ${hasSiteAccess})
  `,
}).link(siteContacts);
// Policy for Documents (General Documents - based on organization_id and is_public)
export const documentsSelectRLS = pgPolicy('documents_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR is_public = TRUE
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR ${hasViewerOrgAccess}
  `,
}).link(documents);
export const documentsInsertRLS = pgPolicy('documents_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(documents);
export const documentsUpdateRLS = pgPolicy('documents_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(documents);
export const documentsDeleteRLS = pgPolicy('documents_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(documents);
// Policy for Entity Documents (Polymorphic Links)
export const entityDocumentsSelectRLS = pgPolicy('entity_documents_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR (
        (${hasFMOrgAccess} OR ${hasViewerOrgAccess}) AND (
          (entity_type = ${sql.raw(`'${EntityType.SITE}'::"EntityType"`)} AND has_site_access(entity_id))
          OR (entity_type = ${sql.raw(`'${EntityType.EQUIPMENT}'::"EntityType"`)} AND entity_id IN (
            SELECT id FROM equipment WHERE organization_id = ${currentUserOrgId}
          ))
          OR (entity_type = ${sql.raw(`'${EntityType.USER}'::"EntityType"`)} AND entity_id = ${currentUserId})
          OR (entity_type = ${sql.raw(`'${EntityType.ORGANIZATION}'::"EntityType"`)} AND entity_id = ${currentUserOrgId})
        )
      )
    `,
}).link(entityDocuments);
export const entityDocumentsInsertRLS = pgPolicy('entity_documents_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR (
        ${hasFMOrgAccess} AND (
          (entity_type = ${sql.raw(`'${EntityType.SITE}'::"EntityType"`)} AND has_site_access(entity_id))
          OR (entity_type = ${sql.raw(`'${EntityType.EQUIPMENT}'::"EntityType"`)} AND entity_id IN (SELECT id FROM equipment WHERE organization_id = ${currentUserOrgId}))
          OR (entity_type = ${sql.raw(`'${EntityType.USER}'::"EntityType"`)} AND entity_id = ${currentUserId})
          OR (entity_type = ${sql.raw(`'${EntityType.ORGANIZATION}'::"EntityType"`)} AND entity_id = ${currentUserOrgId})
        )
      )
    `,
}).link(entityDocuments);
export const entityDocumentsUpdateRLS = pgPolicy('entity_documents_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR (
        (${hasFMOrgAccess} OR ${hasViewerOrgAccess}) AND (
          (entity_type = ${sql.raw(`'${EntityType.SITE}'::"EntityType"`)} AND has_site_access(entity_id))
          OR (entity_type = ${sql.raw(`'${EntityType.EQUIPMENT}'::"EntityType"`)} AND entity_id IN (SELECT id FROM equipment WHERE organization_id = ${currentUserOrgId}))
          OR (entity_type = ${sql.raw(`'${EntityType.USER}'::"EntityType"`)} AND entity_id = ${currentUserId})
          OR (entity_type = ${sql.raw(`'${EntityType.ORGANIZATION}'::"EntityType"`)} AND entity_id = ${currentUserOrgId})
        )
      )
    `,
    withCheck: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR (
        (${hasFMOrgAccess} OR ${hasViewerOrgAccess}) AND (
          (entity_type = ${sql.raw(`'${EntityType.SITE}'::"EntityType"`)} AND has_site_access(entity_id))
          OR (entity_type = ${sql.raw(`'${EntityType.EQUIPMENT}'::"EntityType"`)} AND entity_id IN (SELECT id FROM equipment WHERE organization_id = ${currentUserOrgId}))
          OR (entity_type = ${sql.raw(`'${EntityType.USER}'::"EntityType"`)} AND entity_id = ${currentUserId})
          OR (entity_type = ${sql.raw(`'${EntityType.ORGANIZATION}'::"EntityType"`)} AND entity_id = ${currentUserOrgId})
        )
      )
    `,
}).link(entityDocuments);
export const entityDocumentsDeleteRLS = pgPolicy('entity_documents_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR (
        ${hasFMOrgAccess} AND (
          (entity_type = ${sql.raw(`'${EntityType.SITE}'::"EntityType"`)} AND has_site_access(entity_id))
          OR (entity_type = ${sql.raw(`'${EntityType.EQUIPMENT}'::"EntityType"`)} AND entity_id IN (SELECT id FROM equipment WHERE organization_id = ${currentUserOrgId}))
          OR (entity_type = ${sql.raw(`'${EntityType.USER}'::"EntityType"`)} AND entity_id = ${currentUserId})
          OR (entity_type = ${sql.raw(`'${EntityType.ORGANIZATION}'::"EntityType"`)} AND entity_id = ${currentUserOrgId})
        )
      )
    `,
}).link(entityDocuments);
// Policy for Notes
// `isPublicNote` is the audience tier (#1595): org roles only ever touch `all`
// notes, so the restricted tiers are invisible to them here AND in Supabase
// Realtime, which enforces this same select policy.
export const notesSelectRLS = pgPolicy('notes_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR (${hasAdminOrgAccess} AND ${isPublicNote})
    OR (${hasFMOrgAccess} AND ${isPublicNote})
    OR (${hasViewerOrgAccess} AND ${isPublicNote})
  `,
}).link(notes);
export const notesInsertRLS = pgPolicy('notes_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
    ${isSuperAdmin}
    OR (${hasAdminOrgAccess} AND ${isPublicNote})
    OR (${hasFMOrgAccess} AND ${isPublicNote})
    OR (${hasViewerOrgAccess} AND ${isPublicNote})
  `,
}).link(notes);
export const notesUpdateRLS = pgPolicy('notes_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR (${hasAdminOrgAccess} AND ${isPublicNote})
    OR (${hasFMOrgAccess} AND ${isPublicNote})
    OR (${hasViewerOrgAccess} AND ${isPublicNote})
  `,
    withCheck: sql `
    ${isSuperAdmin}
    OR (${hasAdminOrgAccess} AND ${isPublicNote})
    OR (${hasFMOrgAccess} AND ${isPublicNote})
    OR (${hasViewerOrgAccess} AND ${isPublicNote})
  `,
}).link(notes);
export const notesDeleteRLS = pgPolicy('notes_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR (${hasAdminOrgAccess} AND ${isPublicNote})
    OR (${hasFMOrgAccess} AND ${isPublicNote})
    OR (${hasViewerOrgAccess} AND ${isPublicNote})
  `,
}).link(notes);
// Policy for Note Recipients
export const noteRecipientsSelectRLS = pgPolicy('note_recipients_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR (${hasViewerOrgAccess} AND user_id = ${currentUserId})
  `,
}).link(noteRecipients);
export const noteRecipientsInsertRLS = pgPolicy('note_recipients_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(noteRecipients);
export const noteRecipientsDeleteRLS = pgPolicy('note_recipients_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
  `,
}).link(noteRecipients);
// Policy for Interventions
export const interventionsSelectRLS = pgPolicy('interventions_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR (${hasViewerOrgAccess} AND created_by = ${currentUserId})
    OR (organization_id = ${currentUserOrgId} AND ${currentUserId} = ANY(assigned_user_ids))
  `,
}).link(interventions);
/**
 * A VIEWER's create scope is NARROWER than their organization (#1652, owner
 * decision 2026-08-28): an actively `site_contacts`-linked site, or a temporary
 * site while the org allows one. Until then this policy read
 * `${hasViewerOrgAccess}` alone, so a crafted request could plant work on a
 * same-org site the VIEWER cannot even read. Mirrored in
 * `InterventionsService._assertCreateLocationScope`; migration
 * `0172_viewer_intervention_insert_rls.sql`. The two helpers are SECURITY
 * DEFINER (defined in that migration) — the policy runs as the caller, who can
 * read neither another member's `site_contacts` rows nor, as a VIEWER, an
 * org-settings column.
 */
export const interventionsInsertRLS = pgPolicy('interventions_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR (
      ${hasViewerOrgAccess}
      AND (
        (site_id IS NOT NULL AND has_active_site_contact(site_id))
        OR (
          site_id IS NULL
          AND is_temporary_site
          AND allows_interventions_without_site(organization_id)
        )
      )
    )
  `,
}).link(interventions);
/**
 * UNCHANGED by #1652, and that is the finding, not an omission.
 *
 * The rule a VIEWER must obey on update is « you may not MOVE this onto a site
 * you are not linked to ». That is a statement about OLD vs NEW, and a
 * `WITH CHECK` only ever sees NEW — so every policy-shaped version of it also
 * refuses writes that move nothing: a title edit, or the dispute a VIEWER came
 * to report (#1169), on any row whose `site_contacts` link has since been
 * revoked. The first attempt did exactly that, and
 * `rls-casl-layer-agreement.integration.spec.ts` §5.4 caught it.
 *
 * The rule therefore lives in a BEFORE UPDATE trigger,
 * `validate_viewer_intervention_site_change()` (migration
 * `0172_viewer_intervention_insert_rls.sql`), whose `WHEN (OLD.site_id IS
 * DISTINCT FROM NEW.site_id)` clause is the "only when it actually changes"
 * this policy cannot express — the same mechanism the status graph already uses
 * (`0090`). Being role-conditioned rather than arm-conditioned, it also covers
 * the LAST arm below, which is role-AGNOSTIC: a VIEWER who self-assigns passes
 * that arm as an assignee and would otherwise walk around the creator arm
 * entirely.
 *
 * Keep the two in step: a change to the VIEWER site matrix must touch the
 * trigger, not this policy.
 */
export const interventionsUpdateRLS = pgPolicy('interventions_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR (${hasViewerOrgAccess} AND created_by = ${currentUserId})
    OR (organization_id = ${currentUserOrgId} AND ${currentUserId} = ANY(assigned_user_ids))
  `,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR (${hasViewerOrgAccess} AND created_by = ${currentUserId})
    OR (organization_id = ${currentUserOrgId} AND ${currentUserId} = ANY(assigned_user_ids))
  `,
}).link(interventions);
export const interventionsDeleteRLS = pgPolicy('interventions_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR (${hasViewerOrgAccess} AND created_by = ${currentUserId})
  `,
}).link(interventions);
// Policy for Intervention Notifications
export const interventionNotificationsSelectRLS = pgPolicy('intervention_notifications_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR ${hasFMOrgAccess}
      OR ${hasViewerOrgAccess}
    `,
}).link(interventionNotifications);
export const interventionNotificationsInsertRLS = pgPolicy('intervention_notifications_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR ${hasFMOrgAccess}
    `,
}).link(interventionNotifications);
export const interventionNotificationsUpdateRLS = pgPolicy('intervention_notifications_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR ${hasFMOrgAccess}
    `,
    withCheck: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR ${hasFMOrgAccess}
    `,
}).link(interventionNotifications);
export const interventionNotificationsDeleteRLS = pgPolicy('intervention_notifications_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR ${hasFMOrgAccess}
    `,
}).link(interventionNotifications);
// Policy for Intervention Reports
export const interventionReportsSelectRLS = pgPolicy('intervention_reports_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR organization_id = ${currentUserOrgId}
    `,
}).link(interventionReports);
export const interventionReportsInsertRLS = pgPolicy('intervention_reports_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
      ${isSuperAdmin}
      OR organization_id = ${currentUserOrgId}
    `,
}).link(interventionReports);
export const interventionReportsUpdateRLS = pgPolicy('intervention_reports_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR organization_id = ${currentUserOrgId}
    `,
}).link(interventionReports);
export const interventionReportsDeleteRLS = pgPolicy('intervention_reports_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR organization_id = ${currentUserOrgId}
    `,
}).link(interventionReports);
// Policy for Technician Intervention Actions
// Organization isolation: Only see actions for your organization's interventions
// UPDATE and DELETE policies live only in migrations (0069, 0191) — change all
// four together: `recordActionWithRLS` upserts, so a conflict runs under UPDATE.
export const technicianInterventionActionsSelectRLS = pgPolicy('technician_intervention_actions_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR intervention_id IN (
        SELECT id FROM ${interventions} WHERE organization_id = ${currentUserOrgId}
      )
    `,
}).link(technicianInterventionActions);
export const technicianInterventionActionsInsertRLS = pgPolicy('technician_intervention_actions_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
      ${isSuperAdmin}
      OR intervention_id IN (
        SELECT id FROM ${interventions} WHERE organization_id = ${currentUserOrgId}
      )
    `,
}).link(technicianInterventionActions);
// Policy for Activity Logs
export const activityLogsSelectRLS = pgPolicy('activity_logs_select_rls', {
    for: 'select',
    to: authenticatedRole,
    // DRAFT invoices are a SUPER_ADMIN-only surface (issued-only visibility for
    // org roles — see invoices/AGENTS.md). `invoice.created` rows must stay
    // SUPER_ADMIN-only at the DB level so the org-visible feed never leaks a
    // draft over Supabase Realtime (which enforces this SELECT policy, not the
    // REST `_draftVisibilityConditions` filter). Org roles see `invoice.sent`
    // once the invoice is issued.
    using: sql `
    ${isSuperAdmin}
    OR (
      (${hasAdminOrgAccess} OR ${hasFMOrgAccess} OR ${hasViewerOrgAccess})
      AND action_type <> ${sql.raw(`'${INVOICE_EVENTS.CREATED}'`)}
    )
  `,
}).link(activityLogs);
export const activityLogsInsertRLS = pgPolicy('activity_logs_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
    ${isSuperAdmin}
    OR ${hasAdminOrgAccess}
    OR ${hasFMOrgAccess}
    OR ${hasViewerOrgAccess}
  `,
}).link(activityLogs);
export const activityLogsUpdateRLS = pgPolicy('activity_logs_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(activityLogs);
export const activityLogsDeleteRLS = pgPolicy('activity_logs_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(activityLogs);
// INVOICES & QUOTES RLS POLICIES - SUPER_ADMIN ONLY
// Invoices RLS
export const invoicesSelectRLS = pgPolicy('invoices_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(invoices);
export const invoicesInsertRLS = pgPolicy('invoices_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(invoices);
export const invoicesUpdateRLS = pgPolicy('invoices_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(invoices);
export const invoicesDeleteRLS = pgPolicy('invoices_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(invoices);
// Quotes RLS
export const quotesSelectRLS = pgPolicy('quotes_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(quotes);
export const quotesInsertRLS = pgPolicy('quotes_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(quotes);
export const quotesUpdateRLS = pgPolicy('quotes_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(quotes);
export const quotesDeleteRLS = pgPolicy('quotes_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(quotes);
// Quote decision scores RLS (#1585) — SELECT only for the tenant role, and
// only for SUPER_ADMIN. The recommendation is Depanneo's internal reasoning
// about a subcontractor (reliability flags, commission margin), never client
// -facing: no org role may read it. INSERT/UPDATE/DELETE have NO authenticated
// policy on purpose — the only writer is the event listener on the service-role
// admin client, and a stored score is append-only. See
// `apps/api/src/drizzle/rls-classification.ts` COMMAND_EXEMPTIONS.
export const quoteDecisionScoresSelectRLS = pgPolicy('quote_decision_scores_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(quoteDecisionScores);
// Organization price list (#2964): SUPER_ADMIN reads and writes, nobody
// deletes — soft delete is an UPDATE. COMMAND_EXEMPTIONS records the DELETE.
export const organizationPriceItemsSelectRLS = pgPolicy('organization_price_items_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(organizationPriceItems);
export const organizationPriceItemsInsertRLS = pgPolicy('organization_price_items_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(organizationPriceItems);
export const organizationPriceItemsUpdateRLS = pgPolicy('organization_price_items_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(organizationPriceItems);
// Line Items RLS
export const lineItemsSelectRLS = pgPolicy('line_items_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(lineItems);
export const lineItemsInsertRLS = pgPolicy('line_items_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(lineItems);
export const lineItemsUpdateRLS = pgPolicy('line_items_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(lineItems);
export const lineItemsDeleteRLS = pgPolicy('line_items_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(lineItems);
// Platform Billing Entity RLS
export const platformBillingEntitySelectRLS = pgPolicy('platform_billing_entity_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(platformBillingEntity);
export const platformBillingEntityUpdateRLS = pgPolicy('platform_billing_entity_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(platformBillingEntity);
// TECHNICIAN TABLES RLS POLICIES - SUPER_ADMIN ONLY
// Technicians are global resources (no organization_id). Access controlled by CASL.
// App always uses drizzle.admin. These policies provide defense-in-depth.
// Technicians RLS
export const techniciansSelectRLS = pgPolicy('technicians_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(technicians);
export const techniciansInsertRLS = pgPolicy('technicians_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(technicians);
export const techniciansUpdateRLS = pgPolicy('technicians_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(technicians);
export const techniciansDeleteRLS = pgPolicy('technicians_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(technicians);
// Equipment Catalog RLS — SUPER_ADMIN-only, org-less global catalog (mirrors
// technicians). ENABLE RLS + the authenticated GRANT live in migration 0137;
// these pgPolicy() exports keep the Drizzle snapshot in sync with the applied
// policies (apps/api/AGENTS.md: RLS lives as SQL migrations AND pgPolicy()).
export const equipmentCatalogSelectRLS = pgPolicy('equipment_catalog_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(equipmentCatalog);
export const equipmentCatalogInsertRLS = pgPolicy('equipment_catalog_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(equipmentCatalog);
export const equipmentCatalogUpdateRLS = pgPolicy('equipment_catalog_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(equipmentCatalog);
export const equipmentCatalogDeleteRLS = pgPolicy('equipment_catalog_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(equipmentCatalog);
// Technician Availability RLS
export const technicianAvailabilitySelectRLS = pgPolicy('technician_availability_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(technicianAvailability);
export const technicianAvailabilityInsertRLS = pgPolicy('technician_availability_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(technicianAvailability);
export const technicianAvailabilityUpdateRLS = pgPolicy('technician_availability_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(technicianAvailability);
export const technicianAvailabilityDeleteRLS = pgPolicy('technician_availability_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(technicianAvailability);
// Technician Work Zones RLS
export const technicianWorkZonesSelectRLS = pgPolicy('technician_work_zones_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(technicianWorkZones);
export const technicianWorkZonesInsertRLS = pgPolicy('technician_work_zones_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(technicianWorkZones);
export const technicianWorkZonesUpdateRLS = pgPolicy('technician_work_zones_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(technicianWorkZones);
export const technicianWorkZonesDeleteRLS = pgPolicy('technician_work_zones_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(technicianWorkZones);
// Technician Contacts RLS
export const technicianContactsSelectRLS = pgPolicy('technician_contacts_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(technicianContacts);
export const technicianContactsInsertRLS = pgPolicy('technician_contacts_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(technicianContacts);
export const technicianContactsUpdateRLS = pgPolicy('technician_contacts_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(technicianContacts);
export const technicianContactsDeleteRLS = pgPolicy('technician_contacts_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(technicianContacts);
// Technician Flags RLS
export const technicianFlagsSelectRLS = pgPolicy('technician_flags_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(technicianFlags);
export const technicianFlagsInsertRLS = pgPolicy('technician_flags_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(technicianFlags);
export const technicianFlagsUpdateRLS = pgPolicy('technician_flags_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(technicianFlags);
export const technicianFlagsDeleteRLS = pgPolicy('technician_flags_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(technicianFlags);
// SEQUENCE TABLES RLS POLICIES - SUPER_ADMIN ONLY
// Sequence tables are global (no organization_id).
// App uses drizzle.admin for atomic increment operations.
// Invoice Sequences RLS
export const invoiceSequencesSelectRLS = pgPolicy('invoice_sequences_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(invoiceSequences);
export const invoiceSequencesInsertRLS = pgPolicy('invoice_sequences_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(invoiceSequences);
export const invoiceSequencesUpdateRLS = pgPolicy('invoice_sequences_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(invoiceSequences);
export const invoiceSequencesDeleteRLS = pgPolicy('invoice_sequences_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(invoiceSequences);
// Payment reconciliation (#1280) — SUPER_ADMIN-only on both tables, all four
// verbs. The service runs on `drizzle.admin` (RLS bypassed, like the rest of the
// invoices domain), so these policies are the defense for any direct/RLS access.
export const paymentReconciliationsSelectRLS = pgPolicy('payment_reconciliations_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(paymentReconciliations);
export const paymentReconciliationsInsertRLS = pgPolicy('payment_reconciliations_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(paymentReconciliations);
export const paymentReconciliationsUpdateRLS = pgPolicy('payment_reconciliations_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(paymentReconciliations);
export const paymentReconciliationsDeleteRLS = pgPolicy('payment_reconciliations_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(paymentReconciliations);
export const paymentReconciliationItemsSelectRLS = pgPolicy('payment_reconciliation_items_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(paymentReconciliationItems);
export const paymentReconciliationItemsInsertRLS = pgPolicy('payment_reconciliation_items_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(paymentReconciliationItems);
export const paymentReconciliationItemsUpdateRLS = pgPolicy('payment_reconciliation_items_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(paymentReconciliationItems);
export const paymentReconciliationItemsDeleteRLS = pgPolicy('payment_reconciliation_items_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(paymentReconciliationItems);
// Intervention Sequences RLS
// Unlike invoice/quote sequences (global, SUPER_ADMIN-only via drizzle.admin),
// intervention sequences are org-scoped and incremented INSIDE the RLS
// intervention-create transaction, so every role allowed to create an
// intervention (interventions_insert_rls) must be able to upsert its own
// org's counter row. No delete for non-super-admins.
export const interventionSequencesSelectRLS = pgPolicy('intervention_sequences_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR ${hasFMOrgAccess}
      OR ${hasViewerOrgAccess}
    `,
}).link(interventionSequences);
export const interventionSequencesInsertRLS = pgPolicy('intervention_sequences_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR ${hasFMOrgAccess}
      OR ${hasViewerOrgAccess}
    `,
}).link(interventionSequences);
export const interventionSequencesUpdateRLS = pgPolicy('intervention_sequences_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR ${hasFMOrgAccess}
      OR ${hasViewerOrgAccess}
    `,
    withCheck: sql `
      ${isSuperAdmin}
      OR ${hasAdminOrgAccess}
      OR ${hasFMOrgAccess}
      OR ${hasViewerOrgAccess}
    `,
}).link(interventionSequences);
export const interventionSequencesDeleteRLS = pgPolicy('intervention_sequences_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(interventionSequences);
// Quote Sequences RLS
export const quoteSequencesSelectRLS = pgPolicy('quote_sequences_select_rls', {
    for: 'select',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(quoteSequences);
export const quoteSequencesInsertRLS = pgPolicy('quote_sequences_insert_rls', {
    for: 'insert',
    to: authenticatedRole,
    withCheck: sql `${isSuperAdmin}`,
}).link(quoteSequences);
export const quoteSequencesUpdateRLS = pgPolicy('quote_sequences_update_rls', {
    for: 'update',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
    withCheck: sql `${isSuperAdmin}`,
}).link(quoteSequences);
export const quoteSequencesDeleteRLS = pgPolicy('quote_sequences_delete_rls', {
    for: 'delete',
    to: authenticatedRole,
    using: sql `${isSuperAdmin}`,
}).link(quoteSequences);
// ============================================================================
// Global search (migration 0110 = these tables; 0111_global_search = the trigram GIN
// indexes, text-search configs, can_read_X predicates, RLS policies, indexers, triggers,
// synonyms seed, lexeme MV, search_suggest and global_search RPC — all custom SQL).
// ============================================================================
const tsvector = customType({
    dataType() {
        return 'tsvector';
    },
});
export const searchEntityTypeEnum = pgEnum('SearchEntityType', [
    'intervention',
    'site',
    'equipment',
    'technician',
]);
/** Size of the meaning vector of an intervention document (gemini-embedding-2, #2711). */
export const SEARCH_EMBEDDING_DIMENSIONS = 3072;
/**
 * Denormalized, trigger-maintained global-search registry. Search visibility ≡ entity SELECT
 * visibility, enforced by RLS policies that delegate to can_read_X() (migration 0111). The
 * tsvector columns are populated by the SECURITY DEFINER indexer functions, never by the app.
 */
export const searchDocuments = pgTable('search_documents', {
    entityType: searchEntityTypeEnum('entity_type').notNull(),
    entityId: uuid('entity_id').notNull(),
    organizationId: uuid('organization_id'), // NULL for global entities (technicians)
    siteId: uuid('site_id'),
    createdBy: uuid('created_by'),
    assignedUserIds: uuid('assigned_user_ids').array().notNull().default([]),
    searchVectorFr: tsvector('search_vector_fr'),
    searchVectorSimple: tsvector('search_vector_simple'),
    identifiers: text('identifiers').notNull().default(''),
    identifiersCompact: text('identifiers_compact').notNull().default(''),
    phoneDigits: text('phone_digits').notNull().default(''),
    contactName: text('contact_name'),
    suggestText: text('suggest_text').notNull().default(''),
    displayTitle: text('display_title'),
    displaySubtitle: text('display_subtitle'),
    status: text('status'),
    statusClass: text('status_class').notNull().default('active'),
    updatedAt: timestamp('updated_at', { withTimezone: true }),
    embeddingText: text('embedding_text'),
    embeddingTextHash: text('embedding_text_hash'),
    embedding: vector('embedding', {
        dimensions: SEARCH_EMBEDDING_DIMENSIONS,
    }),
}, t => [
    primaryKey({ columns: [t.entityType, t.entityId] }),
    index('search_documents_fr_idx').using('gin', t.searchVectorFr),
    index('search_documents_simple_idx').using('gin', t.searchVectorSimple),
    index('search_documents_org_type_idx').on(t.organizationId, t.entityType),
    index('search_documents_created_by_idx').on(t.createdBy),
    index('search_documents_site_idx').on(t.siteId),
    index('search_documents_assigned_idx').using('gin', t.assignedUserIds),
]);
/** Métier synonym groups consumed by build_search_tsquery() (migration 0111). */
export const searchSynonyms = pgTable('search_synonyms', {
    id: uuid('id').primaryKey().defaultRandom(),
    terms: text('terms').array().notNull(),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, t => [index('search_synonyms_terms_idx').using('gin', t.terms)]);
/** Per-search telemetry; written only by the SECURITY DEFINER log_search_event() (migration 0111). */
export const searchEvents = pgTable('search_events', {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organization_id'),
    userId: uuid('user_id'),
    query: text('query'),
    normalizedQuery: text('normalized_query'),
    resultCount: integer('result_count').notNull().default(0),
    topBand: integer('top_band'),
    zeroResult: boolean('zero_result').notNull().default(false),
    sectionFilter: searchEntityTypeEnum('section_filter'),
    outcome: text('outcome'),
    openedEntityType: searchEntityTypeEnum('opened_entity_type'),
    openedRank: integer('opened_rank'),
    meaningCount: integer('meaning_count').notNull().default(0),
    openedMeaning: boolean('opened_meaning'),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
}, t => [
    index('search_events_created_idx').on(t.createdAt),
    check('search_events_outcome_check', sql `${t.outcome} IS NULL OR ${t.outcome} IN ('opened', 'none')`),
    check('search_events_opened_check', sql `(${t.outcome} IS NOT DISTINCT FROM 'opened' AND ${t.openedEntityType} IS NOT NULL AND ${t.openedRank} IS NOT NULL)
        OR (${t.outcome} IS DISTINCT FROM 'opened' AND ${t.openedEntityType} IS NULL AND ${t.openedRank} IS NULL)`),
]);
